// MDQ Analyzer — Valantic SCM Expert Team
// Adapted from the valantic_ibp_agent.jsx prototype. Routes through the
// authenticated backend (POST /api/mdq/analyze) — never calls the LLM
// provider directly from the browser.

import React, { useState, useRef, useMemo, useEffect } from "react";
import axios from "axios";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  ArrowRight, ArrowLeft, Upload, FileText, Loader2, RefreshCw, Download,
  ChevronRight, ChevronDown, ChevronUp, AlertCircle, AlertTriangle,
  Layers, Target, GitBranch, Skull, Activity, TrendingUp, Boxes, BarChart3,
  CheckCircle, Database, Cog, Beaker, Car, ShoppingBag, Pill, Cpu,
  UtensilsCrossed, Factory, Zap, Info, Lightbulb, FileDown,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, RadarChart, Radar, PolarGrid, PolarAngleAxis,
} from "recharts";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { generateMdqPdf } from "@/lib/mdqPdfReport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Valantic brand palette ───────────────────────────────────────────────
const V = {
  red: "#FF4B4B", orange: "#FF744F", royal: "#3C4BC8", indigo: "#193773",
  purple: "#5B26B7", gold: "#C0AA81", silver: "#CDCDCD", black: "#100C2A",
  emerald: "#1A9E6B",
};

// ─── Embedded ZSDIDEMAND baseline ─────────────────────────────────────────
const BASELINE_PL = [
  { id: "LOCPRODCUST",            desc: "Location · Product · Customer",            mdts: ["IBPCUSTOMER","IBPLOCATION","IBPPRODUCT","IBPSOURCECUSTOMER"], attrCount: 26 },
  { id: "LOCPRODCUSTWEEKLY",      desc: "Location · Product · Customer · Weekly",   mdts: ["IBPCUSTOMER","IBPLOCATION","IBPPRODUCT","IBPSOURCECUSTOMER"], attrCount: 33 },
  { id: "LOCPRODCUSTDAILY",       desc: "Location · Product · Customer · Daily",    mdts: ["IBPCUSTOMER","IBPLOCATION","IBPPRODUCT","IBPSOURCECUSTOMER"], attrCount: 35 },
  { id: "LOCPRODCUSTMONTHLY",     desc: "Forecast accuracy & model assignment",     mdts: ["IBPCUSTOMER","IBPLOCATION","IBPPRODUCT","IBPSOURCECUSTOMER"], attrCount: 30 },
  { id: "LOCPRODCUSTLAGWEEKLY",   desc: "Lag · Weekly — snapshots",                 mdts: ["IBPCUSTOMER","IBPLAG","IBPLOCATION","IBPPRODUCT"],           attrCount: 34 },
  { id: "LOCPRODCUSTLAGMONTHLY",  desc: "Lag · Monthly — accuracy KPIs",            mdts: ["IBPCUSTOMER","IBPLAG","IBPLOCATION","IBPPRODUCT"],           attrCount: 31 },
  { id: "LOCPRODCUSTDSFULFILLDAILY", desc: "DS Fulfillment · Daily",               mdts: ["IBPCUSTOMER","IBPDSFULFILLMENT","IBPLOCATION","IBPPRODUCT"], attrCount: 36 },
  { id: "LOCPRODCUSTCURRWEEKLY",  desc: "Currency · Weekly — revenue KFs",          mdts: ["IBPCURRENCY","IBPCUSTOMER","IBPLOCATION","IBPPRODUCT"],     attrCount: 35 },
  { id: "LOCPRODCUSTUOMTOWEEKLY", desc: "UoM To · Weekly — alt-UoM KFs",            mdts: ["IBPCUSTOMER","IBPLOCATION","IBPPRODUCT","IBPUOMTO"],        attrCount: 35 },
  { id: "LOCPRODCUSTLAGCALWEEKLY",desc: "Lag · Calendar Weekly — accuracy",         mdts: ["IBPCUSTOMER","IBPLAG","IBPLOCATION","IBPPRODUCT"],          attrCount: 29 },
  { id: "PROMOLOCPRODCUSTWEEKLY", desc: "Promotion disaggregation",                 mdts: ["IBPCUSTOMER","IBPLOCATION","IBPPRODUCT","IBPPROMOTION"],    attrCount: 53 },
  { id: "PROMOPRODCUSTCALWEEKLY", desc: "Promotion entry level",                    mdts: ["IBPCUSTOMER","IBPPRODUCT","IBPPROMOTION"],                  attrCount: 42 },
  { id: "PRODUOMTO",              desc: "UoM conversion factor storage",            mdts: ["IBPPRODUCT","IBPUOMTO"],                                    attrCount: 15 },
  { id: "CURRFRTOWEEKLY",         desc: "Exchange rates",                           mdts: ["IBPCURRENCY","IBPCURRENCYTO"],                              attrCount: 10 },
  { id: "LOCPRODWEEKLY",          desc: "Location · Product — promo split",         mdts: ["IBPLOCATION","IBPPRODUCT"],                                 attrCount: 24 },
];

const BASELINE_KF = [
  { id: "STATISTICALFCSTQTY",      name: "Statistical Forecast Qty",       basePL: "LOCPRODCUSTWEEKLY",        stored: true,  critical: true },
  { id: "ADJDELIVQTY",             name: "Delivered Qty Adjusted",         basePL: "LOCPRODCUSTDAILY",         stored: true,  critical: true },
  { id: "DELIVQTY",                name: "Delivered Qty",                  basePL: "LOCPRODCUSTDAILY",         stored: true,  critical: true },
  { id: "SALESFCSTQTY",            name: "Sales Forecast Qty",             basePL: "LOCPRODCUSTWEEKLY",        stored: true,  critical: true },
  { id: "DEMANDPLANNINGQTY",       name: "Demand Planning Qty",            basePL: "LOCPRODCUSTWEEKLY",        stored: true,  critical: true },
  { id: "CONSENSUSDEMAND",         name: "Consensus Demand (excl. promos)",basePL: "LOCPRODCUSTWEEKLY",        stored: true,  critical: true },
  { id: "FINALCONSENSUSDEMAND",    name: "Consensus Demand (incl. promos)",basePL: "LOCPRODCUSTWEEKLY",        stored: false, critical: true },
  { id: "UOMCONVERSIONFACTOR",     name: "UoM Conversion Factor",          basePL: "PRODUOMTO",                stored: true,  critical: true },
  { id: "EXCHANGERATE",            name: "Exchange Rate",                  basePL: "CURRFRTOWEEKLY",           stored: true,  critical: false },
  { id: "ACTUALSREV",              name: "Actuals Revenue",                basePL: "LOCPRODCUSTCURRWEEKLY",    stored: true,  critical: false },
  { id: "SALESFCSTREV",            name: "Sales Forecast Revenue",         basePL: "LOCPRODCUSTCURRWEEKLY",    stored: false, critical: false },
  { id: "CONFQTY",                 name: "Confirmed Qty",                  basePL: "LOCPRODCUSTDSFULFILLDAILY",stored: true,  critical: false },
  { id: "SENSEDDEMANDQTY",         name: "Sensed Demand Qty",              basePL: "LOCPRODCUSTDAILY",         stored: true,  critical: false },
  { id: "STATISTICALFCSTPRICE",    name: "Statistical Forecast Price",     basePL: "LOCPRODCUSTCURRWEEKLY",    stored: true,  critical: false },
  { id: "PROMOSPLITALL",           name: "Promotion Uplift",               basePL: "PROMOLOCPRODCUSTWEEKLY",   stored: true,  critical: false },
  { id: "FCSTMAPE",                name: "Model Fit Error: MAPE",          basePL: "LOCPRODCUST",              stored: true,  critical: false },
  { id: "CONSENSUSDEMANDSNAPSHOT", name: "Consensus Demand Snapshot",      basePL: "LOCPRODCUSTLAGWEEKLY",     stored: true,  critical: false },
];

const MDT_MAP = {
  IBPPRODUCT: [
    { attr: "PRDID",     mand: true,  s4Tab: "MARA",            s4Fld: "MATNR",            desc: "Product ID — primary key" },
    { attr: "PRDDESCR",  mand: false, s4Tab: "MAKT",            s4Fld: "MAKTX",            desc: "Product description" },
    { attr: "PRDGROUP",  mand: true,  s4Tab: "MARA/ZMATDISPO",  s4Fld: "MATKL/Produkt",    desc: "Product group — hierarchy aggregation" },
    { attr: "UOMID",     mand: true,  s4Tab: "MARA",            s4Fld: "MEINS",            desc: "Base unit of measure" },
    { attr: "ABCID",     mand: true,  s4Tab: "MARC",            s4Fld: "MAABC",            desc: "ABC classification — forecast model assignment" },
    { attr: "XYZID",     mand: true,  s4Tab: "MARC",            s4Fld: "MMSTA",            desc: "Material status / XYZ class" },
    { attr: "CATEGORY",  mand: false, s4Tab: "Segmentation",    s4Fld: "Segmentierung",    desc: "BTS/NTS planning category" },
  ],
  IBPLOCATION: [
    { attr: "LOCID",     mand: true,  s4Tab: "T001W", s4Fld: "WERKS",       desc: "Plant/location ID" },
    { attr: "LOCDESCR",  mand: false, s4Tab: "T001W", s4Fld: "NAME1",       desc: "Location name" },
    { attr: "LOCREGION", mand: true,  s4Tab: "Custom",s4Fld: "SCM_Region",  desc: "Regional hierarchy" },
    { attr: "LOCTYPE",   mand: true,  s4Tab: "Custom",s4Fld: "Custom",      desc: "Location type (Plant/DC/Supplier)" },
  ],
  IBPCUSTOMER: [
    { attr: "CUSTID",      mand: true, s4Tab: "KNA1", s4Fld: "KUNNR", desc: "Customer number" },
    { attr: "CUSTGROUP",   mand: true, s4Tab: "KNVV", s4Fld: "KDGRP", desc: "Customer group" },
    { attr: "CUSTCHANNEL", mand: true, s4Tab: "KNVV", s4Fld: "VTWEG", desc: "Distribution channel" },
    { attr: "CUSTREGION",  mand: false,s4Tab: "KNA1", s4Fld: "REGIO", desc: "Customer region" },
  ],
  IBPUOMTO:       [{ attr: "UOMTOID",          mand: true, s4Tab: "MARM",   s4Fld: "MEINH", desc: "Alternative UoM — feeds 35+ KF formulas" }],
  IBPCURRENCY:    [{ attr: "CURRID",           mand: true, s4Tab: "TCURC",  s4Fld: "WAERS", desc: "Currency — revenue KFs" }],
  IBPLAG:         [{ attr: "LAG",              mand: true, s4Tab: "Custom", s4Fld: "Custom",desc: "Lag ID — snapshot versioning" }],
  IBPDSFULFILLMENT:[{ attr: "DSFULFILLMENTDAYS",mand: true, s4Tab: "LFM1",   s4Fld: "BSTAE", desc: "DS fulfillment offset" }],
  IBPPROMOTION: [
    { attr: "PROMOTIONID",   mand: true, s4Tab: "Custom", s4Fld: "Custom", desc: "Promotion ID" },
    { attr: "STARTDATE",     mand: true, s4Tab: "Custom", s4Fld: "Custom", desc: "Start date" },
    { attr: "ENDDATE",       mand: true, s4Tab: "Custom", s4Fld: "Custom", desc: "End date" },
    { attr: "IBPPROMOSTATUS",mand: true, s4Tab: "Custom", s4Fld: "Custom", desc: "Status (10 = Active gate)" },
  ],
};

const PPDS_INDUSTRIES = {
  chemicals:   { label: "Chemicals / Distribution", Icon: Beaker,           tables: ["MARA","MARC","MKAL","EINA","EORD","CRHD","KAKO","STKO","STPO","PLKO","PLPO"], checks: [
    "Lot size vs packaging unit (25kg / 1000kg / bulk)",
    "PIR lead time > 0 for all external vendors",
    "Country of origin in EINA",
    "Confirmation control Z002 for EDI vendors",
    "Import procedure codes (Verfahren)",
  ]},
  automotive:  { label: "Automotive / Tier-N", Icon: Car, tables: ["MARA","MARC","MKAL","PLKO","PLPO","STKO","STPO","CRHD","KAKO","EKET","EKES"], checks: [
    "JIT/JIS float times (MARC.FHORI)",
    "EDI call-off scheduling (EKET/EKES)",
    "Sequence-dependent setup matrix",
    "Assembly scrap in STPO.AUSSS",
    "MKAL validity dates cover planning horizon",
  ]},
  consumer:    { label: "Consumer Goods / FMCG", Icon: ShoppingBag, tables: ["MARA","MARC","MKAL","STKO","STPO","PLKO","PLPO","CRHD","KAKO","VBAK","VBAP","VBRP"], checks: [
    "Promotion baseline without promo orders",
    "PROMOSPLITALL / IBPPROMOSTATUS = 10",
    "Customer store sell-out data (VMI / POS)",
    "Seasonality: 36+ months history required",
    "Min lot size vs case/pallet pack",
  ]},
  pharma:      { label: "Pharmaceutical / Life Sciences", Icon: Pill, tables: ["MARA","MARC","MKAL","MCHB","STKO","STPO","PLKO","PLPO","CRHD","KAKO","QMEL"], checks: [
    "Shelf life MHDRZ / MHDLP populated",
    "Batch management active (IPRKZ)",
    "QC inspection time in MARC.WEBAZ",
    "Only validated PVs unlocked in MKAL",
    "GMP-compliant routing status (PLKO.STATU = 4)",
  ]},
  hightech:    { label: "High Tech / Electronics", Icon: Cpu, tables: ["MARA","MARC","MKAL","STKO","STPO","PLKO","PLPO","CRHD","KAKO","EINA","EORD","EQUK","MDMA"], checks: [
    "Phase-in / phase-out (MMSTA transitions)",
    "Multi-level BOM accuracy (STPO quantities)",
    "Quota arrangements sum = 100%",
    "Alternative BOMs for substitution",
    "NPI demand ramp-up profile",
  ]},
  food:        { label: "Food & Beverage", Icon: UtensilsCrossed, tables: ["MARA","MARC","MKAL","STKO","STPO","PLKO","PLPO","CRHD","KAKO","MCHB"], checks: [
    "FEFO strategy + expiry dates",
    "Batch yield factor in STPO.AUSCH",
    "36+ months daily history for seasonality",
    "Recipe UoM in production units (KG)",
    "Min lot size vs production batch size",
  ]},
  industrial:  { label: "Industrial Manufacturing", Icon: Factory, tables: ["MARA","MARC","MKAL","STKO","STPO","PLKO","PLPO","CRHD","KAKO","KAKT","EINA","EORD"], checks: [
    "Operation standard values (VGW01/02/03 > 0)",
    "Finite capacity flag for all work centers",
    "Float times for complex multi-op routings",
    "Factory calendar on all KAKO records",
    "Work center texts (KAKT) for alert triage",
  ]},
};

const PRODUCTS = [
  { id: "IBP",  name: "SAP IBP",   Icon: BarChart3, blurb: "Integrated Business Planning · S&OP · Demand · Inventory · Supply & Response" },
  { id: "PPDS", name: "SAP PP/DS", Icon: Cog,       blurb: "Production Planning & Detailed Scheduling · finite scheduling · BOM/Routing · capacity" },
];

// SAP IBP sub-modules — the assessment is scoped to whichever modules are selected.
const IBP_MODULES = [
  { id: "demand",    name: "IBP for Demand",             blurb: "Statistical forecasting · demand sensing · consensus demand",
    kfs: ["STATISTICALFCSTQTY","ADJDELIVQTY","DELIVQTY","SALESFCSTQTY","DEMANDPLANNINGQTY","CONSENSUSDEMAND","FINALCONSENSUSDEMAND","SENSEDDEMANDQTY","FCSTMAPE","PROMOSPLITALL","CONSENSUSDEMANDSNAPSHOT"],
    mdts: ["IBPPRODUCT","IBPLOCATION","IBPCUSTOMER","IBPPROMOTION","IBPLAG"] },
  { id: "supply",    name: "IBP for Supply",             blurb: "Heuristic / optimizer time-series supply planning",
    kfs: ["CONFQTY","STATISTICALFCSTPRICE","UOMCONVERSIONFACTOR"],
    mdts: ["IBPPRODUCT","IBPLOCATION","IBPUOMTO","IBPDSFULFILLMENT"] },
  { id: "inventory", name: "IBP for Inventory",          blurb: "Multi-echelon inventory optimization (MEIO)",
    kfs: ["UOMCONVERSIONFACTOR","DELIVQTY","CONSENSUSDEMAND"],
    mdts: ["IBPPRODUCT","IBPLOCATION","IBPUOMTO"] },
  { id: "sop",       name: "IBP for S&OP",               blurb: "Strategic S&OP · scenarios · financial alignment",
    kfs: ["CONSENSUSDEMAND","FINALCONSENSUSDEMAND","SALESFCSTREV","ACTUALSREV","EXCHANGERATE"],
    mdts: ["IBPPRODUCT","IBPLOCATION","IBPCUSTOMER","IBPCURRENCY"] },
  { id: "response",  name: "IBP for Response & Supply",  blurb: "Order-based supply · allocation · gATP",
    kfs: ["CONFQTY","SENSEDDEMANDQTY","DELIVQTY"],
    mdts: ["IBPPRODUCT","IBPLOCATION","IBPCUSTOMER","IBPDSFULFILLMENT"] },
  { id: "ctrl",      name: "IBP Control Tower",          blurb: "End-to-end visibility · alerts · monitoring",
    kfs: [], mdts: ["IBPPRODUCT","IBPLOCATION","IBPCUSTOMER"] },
];

const STEPS = [
  { id: 1, label: "Configure" },
  { id: 2, label: "Target Structure" },
  { id: 3, label: "Source Files" },
  { id: 4, label: "Run Analysis" },
  { id: 5, label: "Dashboard" },
];

// ─── Utilities ────────────────────────────────────────────────────────────
async function parseFile(file) {
  const isXlsx = /\.xlsx?$/i.test(file.name);
  return new Promise((resolve) => {
    const reader = new FileReader();
    if (isXlsx) {
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
          resolve({ name: file.name, type: "xlsx", headers: rows.length ? Object.keys(rows[0]) : [], rows, size: file.size });
        } catch { resolve({ name: file.name, type: "error", headers: [], rows: [], size: file.size }); }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = (e) => {
        try {
          // Try each candidate delimiter and keep the parse with the highest field count.
          // Without this, a comma-CSV forced to parse with ';' returns a single useless field per row.
          const text = e.target.result;
          const candidates = [",", ";", "\t", "|"];
          let best = { fields: [], rows: [], score: 0 };
          for (const delim of candidates) {
            const r = Papa.parse(text, { header: true, skipEmptyLines: true, delimiter: delim });
            const fields = r.meta?.fields || [];
            const score = fields.length;
            if (score > best.score) best = { fields, rows: r.data || [], score };
          }
          // Final fallback: let PapaParse auto-detect (no forced delimiter).
          if (best.score <= 1) {
            const r = Papa.parse(text, { header: true, skipEmptyLines: true });
            const fields = r.meta?.fields || [];
            if (fields.length > best.score) best = { fields, rows: r.data || [], score: fields.length };
          }
          resolve({ name: file.name, type: "csv", headers: best.fields, rows: best.rows, size: file.size });
        } catch { resolve({ name: file.name, type: "error", headers: [], rows: [], size: file.size }); }
      };
      reader.readAsText(file, "utf-8");
    }
  });
}

function parsePLCSV(text) {
  const r = Papa.parse(text, { header: true, skipEmptyLines: true, delimiter: ";" });
  const lvls = {};
  (r.data || []).forEach((row) => {
    const pl = row["Planning Level"] || "";
    if (!pl) return;
    if (!lvls[pl]) lvls[pl] = { id: pl, desc: row["Description"] || "", mdts: new Set(), attrCount: 0 };
    if (row["Master Data Type ID"]) lvls[pl].mdts.add(row["Master Data Type ID"]);
    if (row["Attribute ID"]) lvls[pl].attrCount++;
  });
  return Object.values(lvls).map((v) => ({ ...v, mdts: [...v.mdts] }));
}

function parseKFCSV(text) {
  const r = Papa.parse(text, { header: true, skipEmptyLines: true, delimiter: ";" });
  const seen = new Set();
  return (r.data || []).filter((row) => {
    const id = row["ID"] || "";
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  }).map((row) => ({
    id: row["ID"] || "",
    name: row["Name"] || "",
    basePL: row["Base Planning Level"] || "",
    stored: (row["Stored Key Figure"] || "") === "X",
    critical: false,
  }));
}

function emptiesSummary(rows, headers) {
  if (!rows.length) return "";
  return headers
    .map((h) => {
      const e = rows.filter((r) => !r[h] || r[h] === "" || r[h] === "0" || r[h] === 0).length;
      return { h, pct: Math.round((e / rows.length) * 100) };
    })
    .filter((x) => x.pct > 50)
    .slice(0, 8)
    .map((x) => `${x.h}=${x.pct}%`)
    .join(", ");
}

const sevColor = (s) =>
  s === "critical" ? "bg-rose-50 text-rose-700 border-rose-200"
  : s === "high"   ? "bg-amber-50 text-amber-700 border-amber-200"
  : s === "medium" ? "bg-blue-50 text-blue-700 border-blue-200"
  : "bg-emerald-50 text-emerald-700 border-emerald-200";

const sevText = (s) =>
  s === "critical" ? "text-rose-700"
  : s === "high"   ? "text-amber-700"
  : s === "medium" ? "text-blue-700"
  : "text-emerald-700";

// ═══════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════
export default function MdqAnalyzer({ onOpenHistory }) {
  const [step, setStep] = useState(1);
  const [product, setProduct] = useState("IBP");
  const [industry, setIndustry] = useState("chemicals");
  const [ibpModules, setIbpModules] = useState(["demand","supply","inventory","sop","response","ctrl"]); // default: all IBP modules
  const [targetPL, setTargetPL] = useState(null);   // null → use BASELINE_PL
  const [targetKF, setTargetKF] = useState(null);   // null → use BASELINE_KF
  const [targetMsg, setTargetMsg] = useState("");
  const [sourceFiles, setSourceFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState("");
  const [trace, setTrace] = useState([]);   // live trace log for Step 4
  const [analysis, setAnalysis] = useState(null);
  const [warning, setWarning] = useState(null);

  // Scope KFs and MDTs to selected IBP modules (for IBP only). Falls back to full baseline if none selected or PP/DS.
  const moduleKfSet = useMemo(() => {
    if (product !== "IBP") return null;
    const ids = new Set(IBP_MODULES.filter((m) => ibpModules.includes(m.id)).flatMap((m) => m.kfs));
    return ids.size ? ids : null;
  }, [product, ibpModules]);
  const moduleMdtSet = useMemo(() => {
    if (product !== "IBP") return null;
    const ids = new Set(IBP_MODULES.filter((m) => ibpModules.includes(m.id)).flatMap((m) => m.mdts));
    return ids.size ? ids : null;
  }, [product, ibpModules]);

  // ⚠️ No baseline pre-population — user must upload PLEVELS + KEYFIGURES extracts.
  // activePL / activeKF stay empty until a target structure file has been parsed.
  const activePL = useMemo(() => {
    const base = targetPL || [];
    if (!moduleMdtSet) return base;
    return base.filter((pl) => pl.mdts.some((m) => moduleMdtSet.has(m)));
  }, [targetPL, moduleMdtSet]);
  const activeKF = useMemo(() => {
    const base = targetKF || [];
    if (!moduleKfSet) return base;
    return base.filter((k) => moduleKfSet.has(k.id));
  }, [targetKF, moduleKfSet]);
  const areaName = targetPL ? "Custom Planning Area" : "—";
  const targetReady = (targetPL && targetPL.length > 0) || (targetKF && targetKF.length > 0);
  const allMDTs = useMemo(() => [...new Set(activePL.flatMap((p) => p.mdts))].sort(), [activePL]);
  const selectedModuleNames = useMemo(
    () => IBP_MODULES.filter((m) => ibpModules.includes(m.id)).map((m) => m.name),
    [ibpModules]
  );

  const traceLog = (msg, kind = "info") => {
    setTrace((prev) => [...prev, { t: new Date(), msg, kind }]);
    setLoadMsg(msg);
  };

  const runAnalysis = async () => {
    setLoading(true);
    setStep(4);
    setTrace([]);
    setWarning(null);

    const filesPayload = sourceFiles.map((f) => ({
      name: f.name,
      rows: f.rows.length,
      headers: f.headers,
      emptiesSummary: emptiesSummary(f.rows, f.headers),
    }));

    traceLog("Assembling valantic SCM Expert Team…");
    await new Promise((r) => setTimeout(r, 200));
    traceLog(`Scope · product=${product}${product === "IBP" ? ` · modules=[${selectedModuleNames.join(", ") || "all"}]` : ` · industry=${industry}`}`);
    traceLog(`Target structure · ${activePL.length} planning levels · ${activeKF.length} key figures (${areaName})`);
    traceLog(`Source data · ${sourceFiles.length} file(s), ${sourceFiles.reduce((s, f) => s + (f.rows?.length || 0), 0).toLocaleString()} total rows`);
    sourceFiles.slice(0, 8).forEach((f) => traceLog(`  ↳ ${f.name} · ${f.rows.length.toLocaleString()} rows · ${f.headers.length} cols`, "file"));

    const phases = [
      { label: "Supply Planning Architect · evaluating CIF / planning area readiness", at: 700 },
      { label: "Industry Expert · applying sector-specific master data rules",        at: 1500 },
      { label: "Digital Process Expert · tracing demand / supply KF cascade",          at: 2400 },
      { label: "Master Data Expert · mapping S/4HANA attributes to MDTs",              at: 3300 },
      { label: "Cross-validating findings against uploaded extracts",                  at: 4500 },
      { label: "Computing severity, IBP impact and remediation roadmap",                at: 6000 },
    ];
    const timers = phases.map((p) => setTimeout(() => traceLog(`✦ ${p.label}…`, "phase"), p.at));

    const t0 = Date.now();
    try {
      traceLog("POST /api/mdq/analyze · awaiting expert team response…", "net");
      const { data } = await axios.post(
        `${API}/mdq/analyze`,
        {
          product,
          industry,
          modules: product === "IBP" ? ibpModules : [],
          targetArea: {
            name: areaName,
            levels: activePL.map((p) => p.id),
            kfs: activeKF.map((k) => k.id),
            modules: product === "IBP" ? selectedModuleNames : [],
          },
          sourceFiles: filesPayload,
        },
        { timeout: 120000 }
      );
      timers.forEach(clearTimeout);
      const dt = ((Date.now() - t0) / 1000).toFixed(1);
      traceLog(`✓ Analysis received in ${dt}s`, "ok");
      if (data.warning) {
        traceLog(`⚠ ${data.warning}`, "warn");
      }
      const a = data.analysis || {};
      traceLog(`✓ Score ${a?.summary?.overallScore ?? "—"} · ${a?.summary?.criticalGaps ?? 0} critical · ${a?.summary?.highGaps ?? 0} high gaps`, "ok");
      traceLog("✓ Persisted to assessment vault", "ok");
      setAnalysis(data.analysis);
      setWarning(data.warning || null);
      await new Promise((r) => setTimeout(r, 600));
      setStep(5);
    } catch (e) {
      timers.forEach(clearTimeout);
      const msg = e?.response?.data?.detail || e?.message || "Analysis failed";
      traceLog(`✗ ${msg}`, "err");
      toast.error("Analysis failed", { description: msg });
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1); setAnalysis(null); setSourceFiles([]); setWarning(null); setTrace([]);
    setTargetPL(null); setTargetKF(null); setTargetMsg("");
  };

  return (
    <div className="min-h-screen bg-valantic-bg">
      {/* Step navigation */}
      <div className="bg-white border-b border-valantic-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <button
                  onClick={() => { if (s.id <= step || (analysis && s.id === 5)) setStep(s.id); }}
                  data-testid={`step-chip-${s.id}`}
                  className={cn(
                    "inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors",
                    step === s.id
                      ? "text-valantic-red border-b-2 border-valantic-red"
                      : step > s.id
                        ? "text-emerald-700 hover:bg-emerald-50"
                        : "text-valantic-muted"
                  )}
                  style={step === s.id ? { borderBottomColor: V.red } : {}}
                >
                  <span className="font-mono text-[10px] opacity-70">0{s.id}</span>
                  <span>{s.label}</span>
                </button>
                {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-valantic-silver" />}
              </React.Fragment>
            ))}
          </div>
          {onOpenHistory && (
            <button
              onClick={onOpenHistory}
              data-testid="open-history-btn"
              className="text-xs px-3 h-8 border border-valantic-border bg-white rounded-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
            >
              <Layers className="w-3.5 h-3.5" strokeWidth={1.75} /> Past analyses
            </button>
          )}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {step === 1 && (
          <Step1
            product={product} setProduct={(p) => { setProduct(p); setAnalysis(null); }}
            industry={industry} setIndustry={setIndustry}
            ibpModules={ibpModules} setIbpModules={setIbpModules}
            onNext={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <Step2
            product={product}
            industry={industry}
            activePL={activePL}
            allMDTs={allMDTs}
            targetMsg={targetMsg} setTargetMsg={setTargetMsg}
            setTargetPL={setTargetPL} setTargetKF={setTargetKF}
            targetReady={targetReady}
            onBack={() => setStep(1)} onNext={() => { if (targetReady) setStep(3); }}
          />
        )}
        {step === 3 && (
          <Step3
            product={product}
            sourceFiles={sourceFiles} setSourceFiles={setSourceFiles}
            onBack={() => setStep(2)}
            onRun={runAnalysis}
          />
        )}
        {(step === 4 || loading) && loading && <Step4Loading loadMsg={loadMsg} trace={trace} />}
        {step === 5 && !loading && analysis && (
          <Step5Dashboard
            analysis={analysis}
            warning={warning}
            product={product}
            areaName={areaName}
            sourceFiles={sourceFiles}
            modules={selectedModuleNames}
            onReset={reset}
          />
        )}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 1 — Configure
// ═══════════════════════════════════════════════════════════════════════════
function Step1({ product, setProduct, industry, setIndustry, ibpModules, setIbpModules, onNext }) {
  const toggleModule = (id) => {
    setIbpModules((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <Hero
        eyebrow="Step 01 · System scope"
        title="SAP Supply Chain Master Data Quality"
        sub="Select your planning system and module — the agent will apply the right expert knowledge and check catalog."
      />

      <Section eyebrow="Planning system" title="Target product">
        <div className="grid md:grid-cols-2 gap-3">
          {PRODUCTS.map((p) => {
            const selected = product === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setProduct(p.id)}
                data-testid={`product-${p.id}-btn`}
                className={cn(
                  "text-left p-5 rounded-md border transition-all",
                  selected
                    ? "border-valantic-red ring-2 ring-valantic-red/20 bg-white"
                    : "border-valantic-border bg-white hover:border-slate-300"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-sm flex items-center justify-center shrink-0",
                    selected ? "val-gradient-warm text-white" : "bg-slate-100 text-slate-600"
                  )}>
                    <p.Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg text-valantic-black">{p.name}</div>
                    <div className="text-xs text-valantic-muted mt-1 leading-relaxed">{p.blurb}</div>
                    {selected && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-valantic-red">
                        <CheckCircle className="w-3 h-3" strokeWidth={2} /> Selected
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Section>

      {product === "IBP" && (
        <Section
          eyebrow="SAP IBP modules"
          title="In-scope sub-modules"
          right={
            <div className="flex gap-1.5">
              <button
                onClick={() => setIbpModules(IBP_MODULES.map((m) => m.id))}
                className="text-[11px] px-2 h-7 border border-valantic-border bg-white rounded-sm hover:bg-slate-50"
                data-testid="ibp-modules-all-btn"
              >Select all</button>
              <button
                onClick={() => setIbpModules([])}
                className="text-[11px] px-2 h-7 border border-valantic-border bg-white rounded-sm hover:bg-slate-50"
                data-testid="ibp-modules-clear-btn"
              >Clear</button>
            </div>
          }
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {IBP_MODULES.map((m) => {
              const selected = ibpModules.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggleModule(m.id)}
                  data-testid={`ibp-module-${m.id}-btn`}
                  className={cn(
                    "text-left p-3 rounded-md border transition-all relative",
                    selected
                      ? "border-valantic-red ring-2 ring-valantic-red/15 bg-white"
                      : "border-valantic-border bg-white hover:border-slate-300"
                  )}
                >
                  <div className="flex items-start gap-2">
                    <div className={cn(
                      "w-5 h-5 mt-0.5 rounded-sm border flex items-center justify-center shrink-0",
                      selected ? "border-valantic-red bg-valantic-red text-white" : "border-slate-300 bg-white"
                    )}
                    style={selected ? { background: V.red, borderColor: V.red } : {}}>
                      {selected && <CheckCircle className="w-3.5 h-3.5" strokeWidth={2.5} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-valantic-black">{m.name}</div>
                      <div className="text-[11px] text-valantic-muted mt-0.5 leading-snug">{m.blurb}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {m.kfs.slice(0, 3).map((k) => (
                          <span key={k} className="font-mono text-[9px] px-1 py-0.5 bg-slate-100 text-slate-700 rounded-sm">{k}</span>
                        ))}
                        {m.kfs.length > 3 && <span className="font-mono text-[9px] text-valantic-muted">+{m.kfs.length - 3} KFs</span>}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {ibpModules.length === 0 && (
            <div className="mt-3 text-xs px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200 rounded-sm">
              No modules selected — analysis will fall back to the full IBP scope.
            </div>
          )}
        </Section>
      )}

      {product === "PPDS" && (
        <Section eyebrow="Sector" title="Industry context">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {Object.entries(PPDS_INDUSTRIES).map(([k, v]) => {
              const selected = industry === k;
              return (
                <button
                  key={k}
                  onClick={() => setIndustry(k)}
                  data-testid={`industry-${k}-btn`}
                  className={cn(
                    "text-left p-3 rounded-md border transition-all",
                    selected
                      ? "border-valantic-royal ring-2 ring-valantic-royal/15 bg-white"
                      : "border-valantic-border bg-white hover:border-slate-300"
                  )}
                  style={selected ? { borderColor: V.royal } : {}}
                >
                  <div className="flex items-start gap-2">
                    <v.Icon className={cn("w-4 h-4 mt-0.5 shrink-0", selected ? "text-valantic-royal" : "text-valantic-muted")} strokeWidth={1.75} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-valantic-black truncate">{v.label}</div>
                      <div className="text-[10px] font-mono text-valantic-muted mt-0.5">
                        {v.tables.length} tables · {v.checks.length} checks
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Section>
      )}

      <div className="flex justify-end">
        <button
          onClick={onNext}
          data-testid="step1-next-btn"
          className="inline-flex items-center gap-2 h-10 px-5 val-gradient-warm text-white rounded-sm text-sm font-semibold hover:shadow-md"
        >
          Continue · Target structure <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 2 — Target structure
// ═══════════════════════════════════════════════════════════════════════════
function Step2({ product, industry, activePL, allMDTs, targetMsg, setTargetMsg, setTargetPL, setTargetKF, targetReady, onBack, onNext }) {
  const dropRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFiles = async (files) => {
    for (const f of files) {
      const text = await new Promise((r) => { const fr = new FileReader(); fr.onload = (ev) => r(ev.target.result); fr.readAsText(f, "utf-8"); });
      const fn = f.name.toLowerCase();
      const pl = parsePLCSV(text);
      const kf = parseKFCSV(text);
      if (fn.includes("plevels") || fn.includes("attr") || pl.length > 3) {
        if (pl.length > 3) { setTargetPL(pl); setTargetMsg(`Loaded ${pl.length} planning levels`); }
      }
      if (fn.includes("keyfig") || fn.includes("kf_") || kf.length > 3) {
        if (kf.length > 3) { setTargetKF(kf); setTargetMsg((p) => `${p ? p + " · " : ""}Loaded ${kf.length} key figures`); }
      }
    }
  };

  const ind = PPDS_INDUSTRIES[industry];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <Hero
        eyebrow="Step 02 · Target structure"
        title={product === "IBP" ? "IBP planning area — target structure" : `${ind?.label} — PP/DS scope`}
        sub={product === "IBP"
          ? "Upload your exported planning area CSVs (PLEVELS + KEYFIGURES). The planning levels in scope are derived from your files — nothing is pre-populated."
          : "Industry-specific data quality checks the agent will apply to your S/4HANA extracts."}
      />

      {product === "IBP" ? (
        <>
          {/* Upload zone */}
          <Section eyebrow="Upload" title="Planning area extract — PLEVELS + KEYFIGURES">
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              {[
                ["SAP6_PLEVELS_ATTRS_*.csv", "Planning levels + MDT attributes"],
                ["ZSDIDEMAND_KEYFIGURES_*.csv", "Key figure definitions"],
              ].map(([fn, desc]) => (
                <div key={fn} className="border border-valantic-border rounded-sm p-3 bg-valantic-bg/40">
                  <div className="font-mono text-xs text-valantic-black truncate">{fn}</div>
                  <div className="text-[11px] text-valantic-muted mt-0.5">{desc}</div>
                </div>
              ))}
            </div>

            <label
              ref={dropRef}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={async (e) => { e.preventDefault(); setDrag(false); await handleFiles([...e.dataTransfer.files]); }}
              className={cn(
                "block border-2 border-dashed rounded-md p-7 text-center cursor-pointer transition-colors",
                drag ? "border-valantic-red bg-rose-50/30" : "border-valantic-border hover:border-valantic-red bg-valantic-bg/30"
              )}
            >
              <Target className="w-7 h-7 mx-auto text-valantic-muted mb-2" strokeWidth={1.5} />
              <div className="font-medium text-valantic-black text-sm">
                Drop planning area CSVs · or click to browse
              </div>
              <div className="text-xs text-valantic-muted mt-1">SAP6_PLEVELS_ATTRS_*.csv · ZSDIDEMAND_KEYFIGURES_*.csv</div>
              <input
                type="file"
                multiple
                accept=".csv,.txt"
                data-testid="upload-target-csvs"
                className="hidden"
                onChange={async (e) => { await handleFiles([...e.target.files]); e.target.value = ""; }}
              />
            </label>

            {targetMsg && (
              <div className="mt-3 text-xs px-3 py-2 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm font-mono" data-testid="target-msg">
                {targetMsg}
              </div>
            )}
            {!targetMsg && (
              <div className="mt-3 text-xs px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200 rounded-sm" data-testid="target-empty">
                ⚠ Upload your planning area extract (PLEVELS + KEYFIGURES CSVs) to define the planning levels in scope. The Continue button stays disabled until at least one valid file is parsed.
              </div>
            )}
          </Section>

          {/* Active PL browser */}
          <Section
            eyebrow={`Active planning area · ${activePL.length} levels`}
            title="Planning levels in scope"
            right={
              <div className="flex flex-wrap gap-1.5">
                {allMDTs.map((m) => (
                  <span key={m} className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-sm">{m}</span>
                ))}
              </div>
            }
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-96 overflow-y-auto ibp-scroll">
              {activePL.map((pl) => (
                <div key={pl.id} className="border border-valantic-border rounded-sm p-2.5 bg-white">
                  <div className="font-mono text-xs font-semibold text-valantic-black truncate">{pl.id}</div>
                  <div className="text-[11px] text-valantic-muted mt-0.5 leading-snug">{pl.desc}</div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {pl.mdts.slice(0, 3).map((m) => (
                        <span key={m} className="font-mono text-[9px] px-1 py-0.5 bg-slate-100 text-slate-700 rounded-sm">{m}</span>
                      ))}
                      {pl.mdts.length > 3 && <span className="font-mono text-[9px] text-valantic-muted">+{pl.mdts.length - 3}</span>}
                    </div>
                    <span className="font-mono text-[10px] text-valantic-muted">{pl.attrCount} attrs</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </>
      ) : (
        <Section eyebrow={ind?.label} title="Industry table scope & checks">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-md val-gradient-warm text-white flex items-center justify-center">
              <ind.Icon className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display font-bold text-xl text-valantic-black">{ind.label}</div>
              <div className="text-xs text-valantic-muted">{ind.tables.length} core tables · {ind.checks.length} sector checks</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-valantic-muted font-mono mb-2">Core tables</div>
              <div className="flex flex-wrap gap-1.5">
                {ind.tables.map((t) => (
                  <span key={t} className="font-mono text-xs px-2 py-0.5 bg-valantic-black text-white rounded-sm">{t}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-valantic-muted font-mono mb-2">Industry checks</div>
              <ol className="space-y-1.5">
                {ind.checks.map((c, i) => (
                  <li key={i} className="text-xs text-slate-800 flex gap-2">
                    <span className="font-mono text-[10px] text-valantic-red mt-0.5 font-bold">{String(i + 1).padStart(2, "0")}</span>
                    {c}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </Section>
      )}

      <NavBar
        onBack={onBack}
        onNext={onNext}
        nextLabel="Continue · Source files"
        nextDisabled={product === "IBP" && !targetReady}
        disabledReason="Upload PLEVELS + KEYFIGURES CSVs to continue"
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 3 — Source files
// ═══════════════════════════════════════════════════════════════════════════
function Step3({ product, sourceFiles, setSourceFiles, onBack, onRun }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);

  const handle = async (files) => {
    setBusy(true);
    const parsed = await Promise.all(files.map(parseFile));
    setSourceFiles((p) => [...p, ...parsed]);
    setBusy(false);
    if (parsed.length) toast.success(`Parsed ${parsed.length} file${parsed.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <Hero
        eyebrow="Step 03 · Source data"
        title="Upload S/4HANA table extracts"
        sub="Drop CSV or XLSX files. The agent will analyze field completeness, plausibility, and cross-system consistency."
      />

      <Section eyebrow="Drop zone" title="S/4HANA table extracts">
        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={async (e) => { e.preventDefault(); setDrag(false); await handle([...e.dataTransfer.files]); }}
          className={cn(
            "block border-2 border-dashed rounded-md p-9 text-center cursor-pointer transition-colors",
            drag ? "border-valantic-red bg-rose-50/30" : "border-valantic-border hover:border-valantic-red bg-valantic-bg/30"
          )}
        >
          <Upload className="w-8 h-8 mx-auto text-valantic-muted mb-2" strokeWidth={1.5} />
          <div className="font-medium text-valantic-black">
            {busy ? "Parsing files…" : "Drop source files — master & transaction data extracts"}
          </div>
          <div className="text-xs text-valantic-muted mt-1">
            {product === "IBP"
              ? "MARA · MARC · ZMM_INFO · MARM · KNA1 · T001W · VBRP · TCURR · Segmentation"
              : "MARC · MKAL · PLKO · PLPO · STKO · STPO · CRHD · KAKO · EINA · EORD"}
          </div>
          <input
            type="file" multiple
            accept=".csv,.xlsx,.xls,.txt"
            data-testid="files-dropzone"
            className="hidden"
            onChange={async (e) => { await handle([...e.target.files]); e.target.value = ""; }}
          />
        </label>
        <div className="text-[11px] text-valantic-muted mt-2">
          Files parsed locally — only column completeness summaries (not row values) are sent to the AI engine.
        </div>
      </Section>

      <Section eyebrow={`Selected · ${sourceFiles.length} file${sourceFiles.length === 1 ? "" : "s"}`} title="Parsed source data">
        {sourceFiles.length === 0 ? (
          <div className="text-sm text-valantic-muted py-6 text-center">
            No files yet — upload at least one S/4HANA extract to enable the analysis.
          </div>
        ) : (
          <div className="border border-valantic-border rounded-sm overflow-hidden">
            <div className="bg-valantic-bg border-b border-valantic-border px-3 py-2 grid grid-cols-12 gap-2 font-mono text-[9px] uppercase tracking-wider text-valantic-muted">
              <div className="col-span-5">File</div>
              <div className="col-span-2 text-right">Rows</div>
              <div className="col-span-1 text-right">Cols</div>
              <div className="col-span-3">{">"}50% empty</div>
              <div className="col-span-1"></div>
            </div>
            {sourceFiles.map((f, i) => (
              <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs items-center border-b border-valantic-border last:border-0">
                <div className="col-span-5 font-mono truncate flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-valantic-muted shrink-0" strokeWidth={1.5} />
                  {f.name}
                </div>
                <div className="col-span-2 text-right font-mono font-bold">{f.rows.length.toLocaleString()}</div>
                <div className="col-span-1 text-right font-mono">{f.headers.length}</div>
                <div className="col-span-3 font-mono text-rose-700 text-[11px] truncate">{emptiesSummary(f.rows, f.headers) || "—"}</div>
                <div className="col-span-1 text-right">
                  <button onClick={() => setSourceFiles((p) => p.filter((_, j) => j !== i))} className="text-xs text-rose-600 hover:text-rose-700">
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-2 h-10 px-4 border border-valantic-border bg-white rounded-sm text-sm hover:bg-slate-50">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Back
        </button>
        <div className="flex items-center gap-3">
          {sourceFiles.length === 0 && (
            <span className="text-xs text-amber-700" data-testid="run-disabled-reason">
              Upload at least one source file to run the analysis
            </span>
          )}
          <button
            onClick={onRun}
            disabled={sourceFiles.length === 0}
            data-testid="run-analysis-btn"
            className={cn(
              "inline-flex items-center gap-2 h-10 px-5 rounded-sm text-sm font-semibold",
              sourceFiles.length === 0
                ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                : "val-gradient-warm text-white hover:shadow-md"
            )}
          >
            <Zap className="w-4 h-4" strokeWidth={1.75} />
            Run expert analysis ({sourceFiles.length} files)
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 4 — Loading
// ═══════════════════════════════════════════════════════════════════════════
function Step4Loading({ loadMsg, trace = [] }) {
  const consoleRef = React.useRef(null);
  React.useEffect(() => {
    if (consoleRef.current) consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [trace.length]);

  const experts = [
    { Icon: Target,    label: "Supply Architect",  color: V.red },
    { Icon: Factory,   label: "Industry Expert",   color: V.orange },
    { Icon: Activity,  label: "Process Expert",    color: V.royal },
    { Icon: Database,  label: "Data Expert",       color: V.indigo },
  ];
  const lineColor = (k) =>
      k === "ok"    ? "text-emerald-700"
    : k === "warn"  ? "text-amber-700"
    : k === "err"   ? "text-rose-700"
    : k === "phase" ? "text-valantic-red"
    : k === "net"   ? "text-blue-700"
    : k === "file"  ? "text-slate-500"
    : "text-slate-700";

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white border border-valantic-border rounded-md p-8 md:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-0.5 val-gradient-warm" />
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-md bg-valantic-black text-white flex items-center justify-center">
            <Loader2 className="w-6 h-6 ibp-spin-slow" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted mb-1">Live trace · expert team in flight</div>
            <h2 className="font-display text-xl md:text-2xl font-bold text-valantic-black tracking-tight">
              valantic SCM Expert Team analysing your data
            </h2>
            <p className="text-xs text-valantic-muted mt-0.5">{loadMsg || "Working…"}</p>
          </div>
        </div>

        {/* Expert chips */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          {experts.map((e, i) => (
            <div key={i} className="border border-valantic-border rounded-sm p-2 bg-valantic-bg/40 flex items-center gap-2">
              <div className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0" style={{ background: e.color, color: "white" }}>
                <e.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
              </div>
              <div className="text-[11px] text-valantic-black font-medium truncate">{e.label}</div>
            </div>
          ))}
        </div>

        {/* Live trace console */}
        <div
          ref={consoleRef}
          data-testid="live-trace-console"
          className="bg-valantic-black text-white font-mono text-[11px] leading-relaxed rounded-sm p-4 h-72 overflow-y-auto border border-valantic-black"
          style={{ fontFamily: "JetBrains Mono, ui-monospace, monospace" }}
        >
          {trace.length === 0 && (
            <div className="text-slate-500">› waiting for trace…</div>
          )}
          {trace.map((t, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-slate-500 shrink-0">
                {String(t.t.getHours()).padStart(2,"0")}:{String(t.t.getMinutes()).padStart(2,"0")}:{String(t.t.getSeconds()).padStart(2,"0")}
              </span>
              <span className={cn("flex-1 break-words", lineColor(t.kind), t.kind === "ok" && "text-emerald-300", t.kind === "warn" && "text-amber-300", t.kind === "err" && "text-rose-300", t.kind === "phase" && "text-orange-300", t.kind === "net" && "text-sky-300", t.kind === "file" && "text-slate-400", (!t.kind || t.kind === "info") && "text-slate-200")}>
                {t.msg}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-3 text-[11px] text-valantic-muted text-center">
          Source rows never leave your browser — only column-level summaries are sent to the AI engine.
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Step 5 — Dashboard
// ═══════════════════════════════════════════════════════════════════════════
const TAB_DEFS = [
  ["overview",     "Overview",         BarChart3],
  ["completeness", "MD Completeness",  CheckCircle],
  ["mds",          "MD Gaps",          AlertTriangle],
  ["tds",          "TXN Gaps",         Activity],
  ["plaus",        "Plausibility",     Target],
  ["erp",          "ERP ↔ IBP",        GitBranch],
  ["dead",         "Dead Objects",     Skull],
  ["mapping",      "Source Mapping",   Database],
  ["kfi",          "KF Readiness",     TrendingUp],
  ["rem",          "Remediation",      Boxes],
];

function Step5Dashboard({ analysis, warning, product, areaName, sourceFiles = [], modules = [], onReset }) {
  const [tab, setTab] = useState("overview");
  const [sevFilter, setSevFilter] = useState("all");
  const sum = analysis.summary || {};
  const ins = analysis.expertInsights || {};
  const mdGaps = (analysis.masterDataGaps || []).filter((g) => sevFilter === "all" || g.sev === sevFilter);
  const tdGaps = (analysis.transactionDataGaps || []).filter((g) => sevFilter === "all" || g.sev === sevFilter);

  const exportCSV = () => {
    const all = [
      ...(analysis.masterDataGaps || []).map((g) => ({ ...g, cat: "MD" })),
      ...(analysis.transactionDataGaps || []).map((g) => ({ ...g, cat: "TD" })),
    ];
    const h = ["cat","id","sev","obj","attr","s4Tab","s4Fld","pct","desc","solution","ibpImpactDemand","ibpImpactSupply"];
    const csv = [h.join(","), ...all.map((r) => h.map((k) => `"${(r[k] || "").toString().replace(/"/g, '""')}"`).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = `valantic_MDQ_Gap_Analysis_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4">
      {warning && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75} />
          <div>{warning}</div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border border-valantic-border rounded-md p-5 md:p-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-0.5 val-gradient-warm" />
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted mb-1 flex items-center gap-2 flex-wrap">
              <span className="font-display font-bold lowercase tracking-tight text-valantic-black text-xs">valantic</span>
              <span className="text-valantic-silver">·</span>
              <span>{product} MDQ Report</span>
              <span className="text-valantic-silver">·</span>
              <span>{areaName}</span>
              <span className="text-valantic-silver">·</span>
              <span>{new Date().toLocaleDateString("en-GB")}</span>
            </div>
            {product === "IBP" && modules.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {modules.map((m) => (
                  <span key={m} className="font-mono text-[10px] px-1.5 py-0.5 bg-valantic-black text-white rounded-sm" data-testid={`scope-module-${m}`}>
                    {m}
                  </span>
                ))}
              </div>
            )}
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-valantic-black tracking-tight">
              Go-live readiness · {sum.goLiveReadiness || "—"}
            </h1>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge tone="critical">{sum.criticalGaps || 0} critical</Badge>
              <Badge tone="high">{sum.highGaps || 0} high</Badge>
              <Badge tone="medium">{sum.mediumGaps || 0} medium</Badge>
              <Badge tone="muted">Remediation · {sum.estimatedRemediation || "—"}</Badge>
              {sum.totalRecords ? <Badge tone="muted">{(sum.totalRecords || 0).toLocaleString()} records</Badge> : null}
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="flex flex-col items-center">
              <div className="font-display text-5xl font-bold leading-none" style={{ color: V.red }}>{sum.overallScore ?? "—"}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-valantic-muted mt-1">DQ score · /100</div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={exportCSV} data-testid="export-csv-btn" className="h-9 px-3 border border-valantic-border bg-white rounded-sm text-xs font-medium hover:bg-slate-50 inline-flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" strokeWidth={1.75} /> Export CSV
              </button>
              <button
                onClick={() => {
                  try {
                    const name = generateMdqPdf({ analysis, product, areaName, modules });
                    toast.success("PDF exported", { description: name });
                  } catch (e) {
                    toast.error("PDF export failed", { description: e?.message || "Unknown error" });
                  }
                }}
                data-testid="export-pdf-btn"
                className="h-9 px-3 border border-valantic-border bg-white rounded-sm text-xs font-medium hover:bg-slate-50 inline-flex items-center gap-1.5"
              >
                <FileDown className="w-3.5 h-3.5" strokeWidth={1.75} /> Export PDF
              </button>
              <button onClick={onReset} data-testid="reset-btn" className="h-9 px-3 bg-valantic-black text-white rounded-sm text-xs font-medium hover:bg-slate-800 inline-flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.75} /> New analysis
              </button>
            </div>
          </div>
        </div>

        {/* Top blockers */}
        {sum.top3Blockers?.length > 0 && (
          <div className="mt-5 border-t border-valantic-border pt-4">
            <div className="text-[10px] uppercase tracking-widest text-valantic-muted font-mono mb-2">Top blockers</div>
            <ol className="space-y-1.5">
              {sum.top3Blockers.slice(0, 3).map((b, i) => (
                <li key={i} className="flex gap-3" data-testid={`top-blocker-${i}`}>
                  <span className="font-mono text-[10px] text-rose-600 mt-1 shrink-0 font-bold">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm text-slate-800 leading-snug">{b}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-5 border-b border-valantic-border flex flex-wrap gap-0 -mb-px overflow-x-auto items-center">
          {TAB_DEFS.map(([k, l, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`result-tab-${k}`}
              className={cn(
                "px-3 py-2.5 text-xs sm:text-sm transition-colors whitespace-nowrap border-b-2 inline-flex items-center gap-1.5",
                tab === k ? "text-valantic-red border-valantic-red font-medium" : "text-valantic-muted border-transparent hover:text-valantic-black"
              )}
              style={tab === k ? { borderBottomColor: V.red, color: V.red } : {}}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
              {l}
            </button>
          ))}
          <select
            value={sevFilter}
            onChange={(e) => setSevFilter(e.target.value)}
            data-testid="severity-filter"
            className="ml-auto text-[11px] px-2 h-7 border border-valantic-border rounded-sm bg-white font-mono"
          >
            <option value="all">All severities</option>
            <option value="critical">Critical only</option>
            <option value="high">High only</option>
            <option value="medium">Medium only</option>
          </select>
        </div>
      </div>

      {tab === "overview"     && <OverviewTab analysis={analysis} ins={ins} sum={sum} modules={modules} product={product} />}
      {tab === "completeness" && <CompletenessTab data={analysis.mdCompleteness || []} sourceFiles={sourceFiles} />}
      {tab === "mds"          && <MdGapsTab items={mdGaps} sourceFiles={sourceFiles} />}
      {tab === "tds"          && <TxnGapsTab items={tdGaps} sourceFiles={sourceFiles} />}
      {tab === "plaus"        && <PlausibilityTab items={analysis.plausibilityChecks || []} sourceFiles={sourceFiles} />}
      {tab === "erp"          && <ConsistencyTab items={analysis.erpConsistencyChecks || []} sourceFiles={sourceFiles} />}
      {tab === "dead"         && <DeadObjectsTab items={analysis.deadObjects || []} sourceFiles={sourceFiles} />}
      {tab === "mapping"      && <MappingTab gaps={analysis.masterDataGaps || []} sourceFiles={sourceFiles} />}
      {tab === "kfi"          && <KpiReadinessTab data={analysis.kpiReadiness || {}} sourceFiles={sourceFiles} />}
      {tab === "rem"          && <RemediationTab data={analysis.remediation || {}} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab views
// ═══════════════════════════════════════════════════════════════════════════
function OverviewTab({ analysis, ins, sum, modules = [], product = "IBP" }) {
  const sevData = [
    { n: "Critical", v: sum.criticalGaps || 0, f: V.red },
    { n: "High",     v: sum.highGaps || 0,     f: V.orange },
    { n: "Medium",   v: sum.mediumGaps || 0,   f: V.royal },
  ];
  const kpiData = Object.entries(analysis.kpiReadiness || {}).slice(0, 7).map(([k, v]) => ({ name: k.slice(0, 14), pct: v.pct || 0 }));
  const mdcData = (analysis.mdCompleteness || []).slice(0, 6).map((m) => ({ name: m.object.slice(0, 14), pct: m.completeness || 0 }));
  const radarData = [
    { s: "Completeness", A: sum.overallScore || 28 },
    { s: "Consistency",  A: 35 },
    { s: "Plausibility", A: 20 },
    { s: "Accuracy",     A: 25 },
    { s: "Timeliness",   A: 15 },
    { s: "Hierarchy",    A: 8 },
  ];
  const kfChain = [
    ["DELIVQTY",             "Actuals",       0],
    ["STATISTICALFCSTQTY",   "Stat. forecast",0],
    ["SALESFCSTQTY",         "Sales fcst",    5],
    ["DEMANDPLANNINGQTY",    "Demand plan",   5],
    ["CONSENSUSDEMAND",      "Consensus",     5],
    ["FINALCONSENSUSDEMAND", "Final",         5],
  ];

  return (
    <div className="space-y-4">
      {/* What's next (guided remediation roadmap) + Run-over-run diff */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <WhatsNextPanel analysis={analysis} sum={sum} modules={modules} product={product} />
        </div>
        <RunDiffPanel currentSum={sum} product={product} modules={modules} />
      </div>

      {/* Expert insights */}
      {(ins.supplyArchitect || ins.industryExpert || ins.processExpert || ins.masterDataExpert) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ExpertCard Icon={Target}    label="Supply Planning Architect"          text={ins.supplyArchitect}  color={V.red} />
          <ExpertCard Icon={Factory}   label="Industry Expert"                    text={ins.industryExpert}   color={V.orange} />
          <ExpertCard Icon={Activity}  label="Digital Supply Chain Process Expert"text={ins.processExpert}    color={V.royal} />
          <ExpertCard Icon={Database}  label="Master Data Expert"                 text={ins.masterDataExpert} color={V.indigo} />
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <ChartCard title="MD completeness · top objects">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={mdcData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3EC" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#6E6A82" }} interval={0} angle={-12} dy={5} />
              <YAxis tick={{ fontSize: 9, fill: "#6E6A82" }} unit="%" />
              <Tooltip />
              <Bar dataKey="pct" fill={V.red} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Severity distribution">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={sevData} dataKey="v" nameKey="n" outerRadius={70} label={(d) => `${d.n}:${d.v}`} labelLine={false}>
                {sevData.map((e, i) => <Cell key={i} fill={e.f} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="DQ dimensions">
          <ResponsiveContainer width="100%" height={200}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#E5E3EC" />
              <PolarAngleAxis dataKey="s" tick={{ fontSize: 9, fill: "#6E6A82" }} />
              <Radar dataKey="A" stroke={V.red} fill={V.red} fillOpacity={0.25} />
            </RadarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="KF readiness">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={kpiData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3EC" />
              <XAxis type="number" tick={{ fontSize: 9, fill: "#6E6A82" }} unit="%" />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: "#6E6A82" }} width={80} />
              <Tooltip />
              <Bar dataKey="pct" radius={[0, 3, 3, 0]}>
                {kpiData.map((d, i) => <Cell key={i} fill={d.pct < 30 ? V.red : d.pct < 70 ? V.orange : V.emerald} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* KF chain flow */}
      <div className="bg-white border border-valantic-border rounded-md p-5">
        <SectionTitle eyebrow="Demand cascade" title="Demand planning KF chain readiness" />
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {kfChain.map(([id, label, pct], i) => (
            <React.Fragment key={id}>
              <div className="border border-valantic-border rounded-sm p-2.5 bg-valantic-bg/40 min-w-[150px]">
                <div className="font-mono text-[10px] font-semibold text-valantic-black truncate">{id.slice(0, 14)}</div>
                <div className="text-[10px] text-valantic-muted mt-0.5">{label}</div>
                <div className={cn("text-lg font-display font-bold mt-1", pct < 30 ? "text-rose-600" : pct < 70 ? "text-amber-600" : "text-emerald-600")}>
                  {pct}%
                </div>
              </div>
              {i < kfChain.length - 1 && <ArrowRight className="w-4 h-4 text-valantic-silver shrink-0" strokeWidth={1.5} />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Excel export helpers ────────────────────────────────────────────────
// Match a source file by table name (e.g., "MARC" → "MARC.csv" / "marc_extract.xlsx").
function findFilesForTable(tableName, sourceFiles) {
  if (!tableName) return [];
  const tokens = tableName.split(/[\/\s,;|]+/).filter((t) => t && t !== "Custom" && t !== "—" && t.length >= 2);
  return sourceFiles.filter((f) => {
    const nm = (f.name || "").toLowerCase();
    return tokens.some((t) => nm.includes(t.toLowerCase()));
  });
}

// Match a header name in a file (case-insensitive, allows trimmed match).
function findHeader(headers, candidate) {
  if (!candidate) return null;
  const t = String(candidate).trim().toLowerCase();
  return headers.find((h) => String(h).trim().toLowerCase() === t)
    || headers.find((h) => String(h).trim().toLowerCase().includes(t))
    || null;
}

// Decide if a value violates a rule. Default rule = "empty or zero".
function isViolation(value, rule = "empty_or_zero") {
  const v = value;
  if (rule === "empty") return v == null || v === "" || String(v).trim() === "";
  if (rule === "zero")  return Number(v) === 0 || v === "0" || v === 0;
  // default: empty or zero
  return v == null || v === "" || String(v).trim() === "" || v === "0" || v === 0 || Number(v) === 0;
}

function inferRuleFromGap(gap) {
  const desc = String(gap.desc || gap.rule || gap.finding || "").toLowerCase();
  if (/=\s*0|=0|>\s*0|must be > 0|leadtime|planlieferzeit|min lot|lot size|rounding|safety stock|eisbe|minbe|mtvfp|plifz/.test(desc)) return "zero";
  if (/empty|null|missing|not (loaded|populated|assigned)|blank/.test(desc)) return "empty";
  return "empty_or_zero";
}

// Build error rows for a single gap by scanning matched files.
// Falls back to the gap's embedded `sampleRecords` when no source file matches.
function collectErrorRows(gap, sourceFiles, fieldKey, tableKey) {
  const tableName = gap[tableKey] || gap.table || "";
  const fieldName = gap[fieldKey] || gap.field || gap.attr || "";
  const rule = inferRuleFromGap(gap);
  const sample = Array.isArray(gap.sampleRecords) ? gap.sampleRecords : [];
  const files = findFilesForTable(tableName, sourceFiles);

  // 1. Try matching against uploaded files.
  for (const f of files) {
    const header = findHeader(f.headers || [], fieldName);
    if (!header) continue;
    const matched = (f.rows || []).filter((r) => isViolation(r[header], rule));
    if (matched.length) {
      return { rows: matched, matchedFile: f.name, fieldUsed: header, rule, note: null };
    }
  }

  // 2. Fall back to the gap's embedded sample error records.
  if (sample.length) {
    return {
      rows: sample,
      matchedFile: "(embedded sample)",
      fieldUsed: fieldName,
      rule,
      note: `Showing ${sample.length} representative records from the gap's embedded sample. Upload "${tableName || "the matching"}" extract to see your live data.`,
    };
  }

  // 3. Nothing to show — explain why.
  if (!files.length) {
    return { rows: [], matchedFile: null, fieldUsed: null, rule,
      note: `No uploaded file matched S/4 table "${tableName || "?"}". Gap detected from reference dataset.` };
  }
  return { rows: [], matchedFile: files[0]?.name, fieldUsed: null, rule,
    note: `Field "${fieldName}" not found in headers of ${files.map((f) => f.name).join(", ")}.` };
}

// Sanitize a string to be a valid Excel sheet name (≤ 31 chars, no \/?*[]: chars).
function safeSheetName(raw, fallback = "Sheet") {
  const cleaned = String(raw || fallback).replace(/[\\\/\?\*\[\]\:]/g, "_").slice(0, 28);
  return cleaned || fallback;
}

// Build & download a multi-sheet workbook.
async function downloadGapsWorkbook({ filename, summaryRows, summaryHeaders, gapSheets }) {
  const wb = XLSX.utils.book_new();
  const sumWs = XLSX.utils.json_to_sheet(summaryRows, { header: summaryHeaders });
  XLSX.utils.book_append_sheet(wb, sumWs, "Summary");
  const used = new Set(["Summary"]);
  for (const sh of gapSheets) {
    let nm = safeSheetName(sh.name);
    let i = 2;
    while (used.has(nm)) { nm = safeSheetName(`${sh.name}_${i++}`); }
    used.add(nm);
    const ws = XLSX.utils.json_to_sheet(sh.rows.length ? sh.rows : [{ Note: sh.note || "No matching error rows" }]);
    XLSX.utils.book_append_sheet(wb, ws, nm);
  }
  XLSX.writeFile(wb, filename);
}

const ExportXlsxButton = ({ onClick, label = "Download Excel", testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 h-8 px-3 border border-valantic-border bg-white rounded-sm text-xs font-medium hover:bg-slate-50"
  >
    <Download className="w-3.5 h-3.5" strokeWidth={1.75} /> {label}
  </button>
);

// Compact icon-only download for individual rows (critical/high).
const RowDownloadButton = ({ onClick, testid, title = "Download error records for this entry" }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    title={title}
    data-testid={testid}
    className="inline-flex items-center gap-1 h-7 px-2 border border-valantic-border bg-white rounded-sm text-[11px] font-medium hover:bg-valantic-red hover:text-white hover:border-valantic-red transition-colors"
  >
    <Download className="w-3 h-3" strokeWidth={1.75} /> XLSX
  </button>
);

// Build & download a single-gap workbook with metadata + matching error rows.
async function downloadSingleGapXlsx({ filename, meta, rows, note }) {
  const wb = XLSX.utils.book_new();
  // Metadata sheet (key/value pairs)
  const metaRows = Object.entries(meta).map(([k, v]) => ({ Field: k, Value: Array.isArray(v) ? v.join(", ") : (v ?? "") }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(metaRows), "Gap");
  // Error rows sheet
  const errRows = rows && rows.length ? rows : [{ Note: note || "No matching error rows found" }];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(errRows), "Error_Records");
  XLSX.writeFile(wb, filename);
}

// Decide whether a gap should expose an individual download (critical + high).
const showRowDownload = (sev) => sev === "critical" || sev === "high";

const ExportToolbar = ({ title, count, onExport, testid, helper }) => (
  <div className="bg-white border border-valantic-border rounded-md px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
    <div className="min-w-0">
      <div className="text-sm font-medium text-valantic-black">{title}</div>
      <div className="text-[11px] text-valantic-muted">{count} entr{count === 1 ? "y" : "ies"} · {helper}</div>
    </div>
    <ExportXlsxButton onClick={onExport} testid={testid} />
  </div>
);

function CompletenessTab({ data, sourceFiles = [] }) {
  const exportXlsx = () => {
    const summaryRows = data.map((c) => ({
      Object: c.object, IBP_Object: c.ibpObject, Source_File: c.sourceFile,
      Completeness_Pct: c.completeness, Critical_Missing: (c.criticalMissing || []).join(" | "),
      IBP_Impact: c.ibpImpact,
    }));
    const gapSheets = [];
    data.forEach((c) => {
      // For each missing attribute, try to find rows where that attribute is empty.
      const files = findFilesForTable(c.sourceFile, sourceFiles);
      const file = files[0] || null;
      (c.criticalMissing || []).forEach((m) => {
        const attr = String(m).split(/[\s—\-=:]/)[0]; // best-effort: take first token
        if (!file) {
          gapSheets.push({ name: `${c.object}_${attr}`, rows: [], note: `No uploaded file matched "${c.sourceFile}". Critical missing: ${m}` });
          return;
        }
        const header = findHeader(file.headers || [], attr);
        if (!header) {
          gapSheets.push({ name: `${c.object}_${attr}`, rows: [], note: `Field "${attr}" not present in ${file.name}. Critical missing: ${m}` });
          return;
        }
        const rows = (file.rows || []).filter((r) => isViolation(r[header], "empty"));
        gapSheets.push({ name: `${c.object}_${attr}`, rows, note: `Empty rows for ${header} in ${file.name}` });
      });
    });
    downloadGapsWorkbook({
      filename: `valantic_MDQ_MD_Completeness_${new Date().toISOString().slice(0, 10)}.xlsx`,
      summaryRows,
      summaryHeaders: ["Object","IBP_Object","Source_File","Completeness_Pct","Critical_Missing","IBP_Impact"],
      gapSheets,
    });
    toast.success("MD Completeness workbook downloaded");
  };

  return (
    <div className="space-y-2.5">
      <ExportToolbar
        title="Master Data Completeness — error records export"
        count={data.length}
        helper={sourceFiles.length ? `${sourceFiles.length} source file(s) matched against gaps` : "No source files uploaded — sheets will reference the gap only"}
        onExport={exportXlsx}
        testid="export-completeness-xlsx-btn"
      />
      <div className="bg-white border border-valantic-border rounded-md overflow-hidden">
        <div className="overflow-x-auto ibp-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-valantic-bg border-b border-valantic-border">
              {["Object","Source","Completeness","Critical missing","IBP impact","Export"].map((h) => (
                <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((c, i) => {
              const severity = c.completeness < 30 ? "critical" : c.completeness < 70 ? "high" : "low";
              const downloadOne = () => {
                const files = findFilesForTable(c.sourceFile, sourceFiles);
                const file = files[0] || null;
                // Collect rows where ANY of the critical missing attributes are empty in matched file.
                let rows = [];
                let note = null;
                if (!file) {
                  note = `No uploaded file matched "${c.sourceFile}". Gap derived from reference dataset.`;
                } else {
                  const attrs = (c.criticalMissing || []).map((m) => String(m).split(/[\s—\-=:]/)[0]).filter(Boolean);
                  const matchedHeaders = attrs.map((a) => ({ a, h: findHeader(file.headers || [], a) })).filter((x) => x.h);
                  if (!matchedHeaders.length) {
                    note = `None of [${attrs.join(", ")}] found in headers of ${file.name}.`;
                  } else {
                    rows = (file.rows || []).filter((r) => matchedHeaders.some(({ h }) => isViolation(r[h], "empty")))
                      .map((r) => ({ ...r, __missing_attributes: matchedHeaders.filter(({ h }) => isViolation(r[h], "empty")).map((x) => x.a).join("|") }));
                  }
                }
                downloadSingleGapXlsx({
                  filename: `valantic_MDQ_Completeness_${c.object.replace(/\s+/g,"_")}_${new Date().toISOString().slice(0, 10)}.xlsx`,
                  meta: {
                    Object: c.object, IBP_Object: c.ibpObject, Source_File: c.sourceFile,
                    Completeness_Pct: c.completeness, Severity_Band: severity,
                    Critical_Missing: c.criticalMissing, IBP_Impact: c.ibpImpact,
                    Matched_File: file?.name || "(none)",
                  },
                  rows, note,
                });
                toast.success(`Downloaded ${c.object}`);
              };
              return (
              <tr key={i} className="border-b border-valantic-border last:border-0 hover:bg-slate-50/60" data-testid={`completeness-row-${i}`}>
                <td className="px-4 py-3 align-top">
                  <div className="font-medium text-valantic-black">{c.object}</div>
                  <div className="text-[10px] font-mono text-valantic-muted mt-0.5">{c.ibpObject}</div>
                </td>
                <td className="px-4 py-3 align-top text-xs font-mono text-slate-700">{c.sourceFile}</td>
                <td className="px-4 py-3 align-top w-44">
                  <div className="flex items-baseline gap-2">
                    <span className={cn("font-display text-xl font-bold", c.completeness < 30 ? "text-rose-600" : c.completeness < 70 ? "text-amber-600" : "text-emerald-600")}>
                      {c.completeness}%
                    </span>
                  </div>
                  <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className={cn("h-full", c.completeness < 30 ? "bg-rose-500" : c.completeness < 70 ? "bg-amber-500" : "bg-emerald-500")} style={{ width: `${c.completeness}%` }} />
                  </div>
                </td>
                <td className="px-4 py-3 align-top">
                  <ul className="text-xs text-slate-700 space-y-0.5">
                    {(c.criticalMissing || []).map((m, j) => <li key={j} className="font-mono">· {m}</li>)}
                  </ul>
                </td>
                <td className="px-4 py-3 align-top text-xs text-slate-700 leading-relaxed max-w-xs">{c.ibpImpact}</td>
                <td className="px-4 py-3 align-top">
                  {showRowDownload(severity) && (
                    <RowDownloadButton onClick={downloadOne} testid={`completeness-row-dl-${i}`} />
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

function MdGapsTab({ items, sourceFiles = [] }) {
  const [open, setOpen] = useState(null);

  const exportXlsx = () => {
    const summaryRows = items.map((g) => ({
      ID: g.id, Severity: g.sev, Object: g.obj, Attribute: g.attr,
      S4_Table: g.s4Tab, S4_Field: g.s4Fld, Empty_Pct: g.pct, Records: g.recs,
      Description: g.desc, Solution: g.solution,
      Impact_Demand: g.ibpImpactDemand, Impact_Supply: g.ibpImpactSupply, Impact_Inventory: g.ibpImpactInventory,
    }));
    const gapSheets = items.map((g) => {
      const { rows, matchedFile, fieldUsed, rule, note } = collectErrorRows(g, sourceFiles, "s4Fld", "s4Tab");
      const enriched = rows.map((r) => ({ ...r, __violation_field: fieldUsed, __violation_rule: rule }));
      return {
        name: `${g.id || g.attr}`,
        rows: enriched,
        note: note || (rows.length ? `${rows.length} rows from ${matchedFile} where ${fieldUsed} violates rule "${rule}"` : "No violating rows found"),
      };
    });
    downloadGapsWorkbook({
      filename: `valantic_MDQ_MD_Gaps_${new Date().toISOString().slice(0, 10)}.xlsx`,
      summaryRows,
      summaryHeaders: ["ID","Severity","Object","Attribute","S4_Table","S4_Field","Empty_Pct","Records","Description","Solution","Impact_Demand","Impact_Supply","Impact_Inventory"],
      gapSheets,
    });
    toast.success("MD Gaps workbook downloaded");
  };

  return (
    <div className="space-y-2.5">
      <ExportToolbar
        title="Master Data Gaps — error records export"
        count={items.length}
        helper={sourceFiles.length ? `Matching against ${sourceFiles.length} uploaded file(s)` : "No source files uploaded — sheets will note the gap only"}
        onExport={exportXlsx}
        testid="export-mds-xlsx-btn"
      />
      {items.map((it, i) => {
        const downloadOne = () => {
          const { rows, matchedFile, fieldUsed, rule, note } = collectErrorRows(it, sourceFiles, "s4Fld", "s4Tab");
          const enriched = rows.map((r) => ({ ...r, __violation_field: fieldUsed, __violation_rule: rule }));
          downloadSingleGapXlsx({
            filename: `valantic_MDQ_${it.id || it.attr}_${new Date().toISOString().slice(0, 10)}.xlsx`,
            meta: {
              ID: it.id, Severity: it.sev, Object: it.obj, Attribute: it.attr,
              S4_Table: it.s4Tab, S4_Field: it.s4Fld, Empty_Pct: it.pct, Records: it.recs,
              Description: it.desc, Solution: it.solution,
              Impact_Demand: it.ibpImpactDemand, Impact_Supply: it.ibpImpactSupply, Impact_Inventory: it.ibpImpactInventory,
              Matched_File: matchedFile || "(none)", Violation_Field: fieldUsed || "(n/a)", Violation_Rule: rule,
            },
            rows: enriched,
            note: note || (rows.length ? `${rows.length} rows from ${matchedFile}` : "No violating rows found"),
          });
          toast.success(`Downloaded ${it.id || it.attr}`);
        };
        return (
        <div key={i} className="bg-white border border-valantic-border rounded-md overflow-hidden">
          <div className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-slate-50/80">
            <button onClick={() => setOpen(open === i ? null : i)} className="flex-1 flex items-center gap-3 text-left" data-testid={`md-gap-${it.id || i}`}>
            <span className={cn("font-mono text-[10px] px-1.5 py-0.5 rounded-sm uppercase font-bold tracking-wider border", sevColor(it.sev))}>{it.sev}</span>
            <span className="font-mono text-xs text-valantic-muted shrink-0">{it.id}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-valantic-black truncate">
                {it.obj}<span className="text-valantic-muted mx-1.5">·</span>{it.attr}
              </div>
              <div className="text-xs text-valantic-muted truncate">{it.desc}</div>
            </div>
            <div className="text-right font-mono text-xs">
              <div className={cn("font-bold", sevText(it.sev))}>{it.pct}%</div>
              <div className="text-[10px] text-valantic-muted">{(it.recs ?? 0).toLocaleString()} recs</div>
            </div>
            </button>
            {showRowDownload(it.sev) && (
              <RowDownloadButton onClick={downloadOne} testid={`md-gap-dl-${it.id || i}`} />
            )}
            <button onClick={() => setOpen(open === i ? null : i)} className="p-1" aria-label="toggle">
              {open === i ? <ChevronUp className="w-4 h-4 text-valantic-muted" /> : <ChevronDown className="w-4 h-4 text-valantic-muted" />}
            </button>
          </div>
          {open === i && (
            <div className="border-t border-valantic-border px-5 py-4 grid md:grid-cols-2 gap-4 bg-valantic-bg/60">
              <Box label="S/4HANA source" value={`${it.s4Tab || "—"}.${it.s4Fld || "—"}`} mono />
              <Box label="Solution" value={it.solution} />
              <Box label="IBP impact · Demand"    value={it.ibpImpactDemand} />
              <Box label="IBP impact · Supply"    value={it.ibpImpactSupply} />
              <Box label="IBP impact · Inventory" value={it.ibpImpactInventory} />
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

function TxnGapsTab({ items, sourceFiles = [] }) {
  const exportXlsx = () => {
    const summaryRows = items.map((t) => ({
      ID: t.id, Severity: t.sev, Domain: t.domain, Description: t.desc,
      Solution: t.solution, IBP_Impact: t.ibpImpact, KFs: (t.kfs || []).join(", "),
    }));
    // TXN gaps don't have explicit s4Tab/s4Fld — parse from description/domain.
    const gapSheets = items.map((t) => {
      const text = `${t.domain} ${t.desc}`;
      const tableMatch = text.match(/\b([A-Z]{2,5}[0-9A-Z_]{0,6})\b/g) || [];
      const fieldGuess = (text.match(/\b([A-Z][A-Z0-9_]{2,15})\b/g) || []).find((w) =>
        !["IBP","KF","UoM","NULL","SAP","CIF","BTS","NTS","DS","KFs","EDI","ABC"].includes(w)
      );
      const candidate = { s4Tab: tableMatch[0] || "", s4Fld: fieldGuess || "", desc: t.desc, finding: t.desc };
      const { rows, matchedFile, fieldUsed, rule, note } = collectErrorRows(candidate, sourceFiles, "s4Fld", "s4Tab");
      const enriched = rows.map((r) => ({ ...r, __violation_field: fieldUsed, __violation_rule: rule }));
      return {
        name: t.id || t.domain,
        rows: enriched,
        note: note || (rows.length ? `${rows.length} rows from ${matchedFile} matching rule "${rule}" on ${fieldUsed}` : "No violating rows found"),
      };
    });
    downloadGapsWorkbook({
      filename: `valantic_MDQ_TXN_Gaps_${new Date().toISOString().slice(0, 10)}.xlsx`,
      summaryRows,
      summaryHeaders: ["ID","Severity","Domain","Description","Solution","IBP_Impact","KFs"],
      gapSheets,
    });
    toast.success("TXN Gaps workbook downloaded");
  };

  return (
    <div className="space-y-3">
      <ExportToolbar
        title="Transaction Data Gaps — error records export"
        count={items.length}
        helper={sourceFiles.length ? `Matching against ${sourceFiles.length} uploaded file(s)` : "No source files uploaded — sheets will note the gap only"}
        onExport={exportXlsx}
        testid="export-tds-xlsx-btn"
      />
      <div className="grid md:grid-cols-2 gap-3">
      {items.map((t, i) => {
        const downloadOne = () => {
          const text = `${t.domain} ${t.desc}`;
          const tableMatch = text.match(/\b([A-Z]{2,5}[0-9A-Z_]{0,6})\b/g) || [];
          const fieldGuess = (text.match(/\b([A-Z][A-Z0-9_]{2,15})\b/g) || []).find((w) =>
            !["IBP","KF","UoM","NULL","SAP","CIF","BTS","NTS","DS","KFs","EDI","ABC"].includes(w)
          );
          const candidate = { s4Tab: tableMatch[0] || "", s4Fld: fieldGuess || "", desc: t.desc, finding: t.desc };
          const { rows, matchedFile, fieldUsed, rule, note } = collectErrorRows(candidate, sourceFiles, "s4Fld", "s4Tab");
          const enriched = rows.map((r) => ({ ...r, __violation_field: fieldUsed, __violation_rule: rule }));
          downloadSingleGapXlsx({
            filename: `valantic_MDQ_${t.id || "TXN"}_${new Date().toISOString().slice(0, 10)}.xlsx`,
            meta: {
              ID: t.id, Severity: t.sev, Domain: t.domain, Description: t.desc,
              Solution: t.solution, IBP_Impact: t.ibpImpact, KFs: (t.kfs || []).join(", "),
              Inferred_Table: candidate.s4Tab || "(none)",
              Inferred_Field: candidate.s4Fld || "(none)",
              Matched_File: matchedFile || "(none)", Violation_Rule: rule,
            },
            rows: enriched,
            note: note || (rows.length ? `${rows.length} rows from ${matchedFile}` : "No violating rows found"),
          });
          toast.success(`Downloaded ${t.id || "TXN gap"}`);
        };
        return (
        <div key={i} className="bg-white border border-valantic-border rounded-md p-4" data-testid={`txn-gap-${t.id || i}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className={cn("font-mono text-[10px] px-1.5 py-0.5 rounded-sm uppercase font-bold tracking-wider border", sevColor(t.sev))}>{t.sev}</span>
            <span className="font-mono text-xs text-valantic-muted">{t.id}</span>
            {showRowDownload(t.sev) && (
              <span className="ml-auto">
                <RowDownloadButton onClick={downloadOne} testid={`txn-gap-dl-${t.id || i}`} />
              </span>
            )}
          </div>
          <div className="font-medium text-valantic-black">{t.domain}</div>
          <p className="text-xs text-slate-700 mt-1.5 leading-relaxed">{t.desc}</p>
          <div className="mt-3 pt-3 border-t border-valantic-border space-y-2">
            <Field label="Solution"   value={t.solution} />
            <Field label="IBP impact" value={t.ibpImpact} />
            {t.kfs && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-valantic-muted mb-1 font-mono">Affected KFs</div>
                <div className="flex flex-wrap gap-1">
                  {t.kfs.map((k) => <span key={k} className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-sm">{k}</span>)}
                </div>
              </div>
            )}
          </div>
        </div>
        );
      })}
      </div>
    </div>
  );
}

function PlausibilityTab({ items, sourceFiles = [] }) {
  const exportXlsx = () => {
    const summaryRows = items.map((p) => ({
      ID: p.id, Severity: p.sev, Table: p.table, Field: p.field, Rule: p.rule,
      Pct_Failing: p.pctFailing, Finding: p.finding, Impact: p.impact, Fix: p.fix,
    }));
    const gapSheets = items.map((p) => {
      const { rows, matchedFile, fieldUsed, rule, note } = collectErrorRows(p, sourceFiles, "field", "table");
      const enriched = rows.map((r) => ({ ...r, __violation_field: fieldUsed, __violation_rule: rule }));
      return {
        name: p.id || p.field,
        rows: enriched,
        note: note || (rows.length ? `${rows.length} rows from ${matchedFile} failing rule "${rule}" on ${fieldUsed}` : "No violating rows found"),
      };
    });
    downloadGapsWorkbook({
      filename: `valantic_MDQ_Plausibility_${new Date().toISOString().slice(0, 10)}.xlsx`,
      summaryRows,
      summaryHeaders: ["ID","Severity","Table","Field","Rule","Pct_Failing","Finding","Impact","Fix"],
      gapSheets,
    });
    toast.success("Plausibility workbook downloaded");
  };

  return (
    <div className="space-y-2.5">
      <ExportToolbar
        title="Plausibility Checks — failing records export"
        count={items.length}
        helper={sourceFiles.length ? `Matching against ${sourceFiles.length} uploaded file(s)` : "No source files uploaded — sheets will note the check only"}
        onExport={exportXlsx}
        testid="export-plaus-xlsx-btn"
      />
      {items.map((p, i) => {
        const downloadOne = () => {
          const { rows, matchedFile, fieldUsed, rule, note } = collectErrorRows(p, sourceFiles, "field", "table");
          const enriched = rows.map((r) => ({ ...r, __violation_field: fieldUsed, __violation_rule: rule }));
          downloadSingleGapXlsx({
            filename: `valantic_MDQ_${p.id || p.field}_${new Date().toISOString().slice(0, 10)}.xlsx`,
            meta: {
              ID: p.id, Severity: p.sev, Table: p.table, Field: p.field, Rule: p.rule,
              Pct_Failing: p.pctFailing, Finding: p.finding, Impact: p.impact, Fix: p.fix,
              Matched_File: matchedFile || "(none)", Violation_Field: fieldUsed || "(n/a)", Violation_Rule: rule,
            },
            rows: enriched,
            note: note || (rows.length ? `${rows.length} rows from ${matchedFile} failing rule "${rule}"` : "No violating rows found"),
          });
          toast.success(`Downloaded ${p.id || p.field}`);
        };
        return (
        <div key={i} className="bg-white border border-valantic-border rounded-md p-4 grid md:grid-cols-12 gap-3" data-testid={`plaus-${p.id || i}`}>
          <div className="md:col-span-3">
            <div className="flex items-center gap-2 mb-1">
              <span className={cn("font-mono text-[10px] px-1.5 py-0.5 rounded-sm uppercase font-bold tracking-wider border", sevColor(p.sev))}>{p.sev}</span>
              <span className="font-mono text-xs text-valantic-muted">{p.id}</span>
              {showRowDownload(p.sev) && (
                <span className="ml-auto">
                  <RowDownloadButton onClick={downloadOne} testid={`plaus-dl-${p.id || i}`} />
                </span>
              )}
            </div>
            <div className="font-mono text-sm font-medium">{p.field}</div>
            <div className="font-mono text-[10px] text-valantic-muted">{p.table}</div>
            <div className={cn("mt-2 font-display text-2xl font-bold", sevText(p.sev))}>{p.pctFailing}%</div>
            <div className="text-[10px] uppercase tracking-wider text-valantic-muted">failing</div>
          </div>
          <div className="md:col-span-9 space-y-2">
            <Field label="Rule"    value={p.rule} />
            <Field label="Finding" value={p.finding} />
            <Field label="Impact"  value={p.impact} />
            <Field label="Fix"     value={p.fix} />
          </div>
        </div>
        );
      })}
    </div>
  );
}

function ConsistencyTab({ items, sourceFiles = [] }) {
  const exportXlsx = () => {
    const summaryRows = items.map((c) => ({
      ID: c.id, Severity: c.sev, ERP_Field: c.erpField, IBP_Field: c.ibpField,
      Finding: c.finding, Deviation_Impact: c.deviationImpact, Resolution: c.resolution,
    }));
    const gapSheets = items.map((c) => {
      // Try ERP side first (e.g., "MARC.PLIFZ"), then IBP side.
      const erpParts = String(c.erpField || "").split(".");
      const candidate = { s4Tab: erpParts[0] || "", s4Fld: erpParts[1] || c.erpField, finding: c.finding, sampleRecords: c.sampleRecords };
      const { rows, matchedFile, fieldUsed, rule, note } = collectErrorRows(candidate, sourceFiles, "s4Fld", "s4Tab");
      return {
        name: c.id || c.erpField,
        rows: rows.map((r) => ({ ...r, __violation_field: fieldUsed, __violation_rule: rule })),
        note: note || (rows.length ? `${rows.length} rows from ${matchedFile}` : "No matching error records"),
      };
    });
    downloadGapsWorkbook({
      filename: `valantic_MDQ_ERP_IBP_Consistency_${new Date().toISOString().slice(0, 10)}.xlsx`,
      summaryRows,
      summaryHeaders: ["ID","Severity","ERP_Field","IBP_Field","Finding","Deviation_Impact","Resolution"],
      gapSheets,
    });
    toast.success("ERP↔IBP workbook downloaded");
  };

  return (
    <div className="space-y-2.5">
      <ExportToolbar
        title="ERP ↔ IBP Consistency — error records export"
        count={items.length}
        helper={sourceFiles.length ? `Matching against ${sourceFiles.length} uploaded file(s)` : "Showing embedded sample records (upload source files for live data)"}
        onExport={exportXlsx}
        testid="export-erp-xlsx-btn"
      />
      {items.map((c, i) => {
        const downloadOne = () => {
          const erpParts = String(c.erpField || "").split(".");
          const candidate = { s4Tab: erpParts[0] || "", s4Fld: erpParts[1] || c.erpField, finding: c.finding, sampleRecords: c.sampleRecords };
          const { rows, matchedFile, fieldUsed, rule, note } = collectErrorRows(candidate, sourceFiles, "s4Fld", "s4Tab");
          downloadSingleGapXlsx({
            filename: `valantic_MDQ_${c.id || "CC"}_${new Date().toISOString().slice(0, 10)}.xlsx`,
            meta: {
              ID: c.id, Severity: c.sev, ERP_Field: c.erpField, IBP_Field: c.ibpField,
              Finding: c.finding, Deviation_Impact: c.deviationImpact, Resolution: c.resolution,
              Matched_File: matchedFile || "(none)", Violation_Field: fieldUsed || "(n/a)", Violation_Rule: rule,
            },
            rows: rows.map((r) => ({ ...r, __violation_field: fieldUsed, __violation_rule: rule })),
            note,
          });
          toast.success(`Downloaded ${c.id || "CC"}`);
        };
        return (
        <div key={i} className="bg-white border border-valantic-border rounded-md p-4" data-testid={`consist-${c.id || i}`}>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={cn("font-mono text-[10px] px-1.5 py-0.5 rounded-sm uppercase font-bold tracking-wider border", sevColor(c.sev))}>{c.sev}</span>
            <span className="font-mono text-xs text-valantic-muted">{c.id}</span>
            <div className="flex items-center gap-2 ml-auto">
              <span className="font-mono text-xs px-2 py-0.5 bg-valantic-black text-white rounded-sm">{c.erpField}</span>
              <ArrowRight className="w-3 h-3 text-valantic-muted" />
              <span className="font-mono text-xs px-2 py-0.5 border border-valantic-border rounded-sm">{c.ibpField}</span>
              {showRowDownload(c.sev) && (
                <RowDownloadButton onClick={downloadOne} testid={`consist-dl-${c.id || i}`} />
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Field label="Finding"          value={c.finding} />
            <Field label="Deviation impact" value={c.deviationImpact} />
            <Field label="Resolution"       value={c.resolution} />
          </div>
        </div>
        );
      })}
    </div>
  );
}

function DeadObjectsTab({ items, sourceFiles = [] }) {
  const exportXlsx = () => {
    const summaryRows = items.map((d) => ({
      ID: d.id, Severity: d.sev, Type: d.type, Rule: d.rule, Count: d.count,
      Evidence: d.evidence, IBP_Impact: d.ibpImpact, Cleanup: d.cleanup,
    }));
    // Dead objects don't always carry an s4Tab — extract from rule text.
    const gapSheets = items.map((d) => {
      const ruleText = String(d.rule || "");
      const tableMatch = ruleText.match(/\b([A-Z]{2,5}[0-9A-Z_]{0,6})\b/g) || [];
      const fieldGuess = (ruleText.match(/\b([A-Z][A-Z0-9_]{2,15})\b/g) || []).find((w) =>
        !["IBP","KF","NULL","SAP","CIF","BTS","NTS","DS","EDI","ABC","BSTAE","BESKZ"].includes(w) && w !== tableMatch[0]
      );
      const candidate = { s4Tab: tableMatch[0] || "", s4Fld: fieldGuess || "", desc: d.rule, finding: d.evidence, sampleRecords: d.sampleRecords };
      const { rows, matchedFile, fieldUsed, rule, note } = collectErrorRows(candidate, sourceFiles, "s4Fld", "s4Tab");
      return {
        name: d.id || d.type,
        rows: rows.map((r) => ({ ...r, __violation_field: fieldUsed, __violation_rule: rule })),
        note: note || (rows.length ? `${rows.length} rows from ${matchedFile}` : "No matching error records"),
      };
    });
    downloadGapsWorkbook({
      filename: `valantic_MDQ_Dead_Objects_${new Date().toISOString().slice(0, 10)}.xlsx`,
      summaryRows,
      summaryHeaders: ["ID","Severity","Type","Rule","Count","Evidence","IBP_Impact","Cleanup"],
      gapSheets,
    });
    toast.success("Dead Objects workbook downloaded");
  };

  return (
    <div className="space-y-3">
      <ExportToolbar
        title="Dead Objects — error records export"
        count={items.length}
        helper={sourceFiles.length ? `Matching against ${sourceFiles.length} uploaded file(s)` : "Showing embedded sample records (upload source files for live data)"}
        onExport={exportXlsx}
        testid="export-dead-xlsx-btn"
      />
      <div className="grid md:grid-cols-2 gap-3">
      {items.map((d, i) => {
        const downloadOne = () => {
          const ruleText = String(d.rule || "");
          const tableMatch = ruleText.match(/\b([A-Z]{2,5}[0-9A-Z_]{0,6})\b/g) || [];
          const fieldGuess = (ruleText.match(/\b([A-Z][A-Z0-9_]{2,15})\b/g) || []).find((w) =>
            !["IBP","KF","NULL","SAP","CIF","BTS","NTS","DS","EDI","ABC","BSTAE","BESKZ"].includes(w) && w !== tableMatch[0]
          );
          const candidate = { s4Tab: tableMatch[0] || "", s4Fld: fieldGuess || "", desc: d.rule, finding: d.evidence, sampleRecords: d.sampleRecords };
          const { rows, matchedFile, fieldUsed, rule, note } = collectErrorRows(candidate, sourceFiles, "s4Fld", "s4Tab");
          downloadSingleGapXlsx({
            filename: `valantic_MDQ_${d.id || "DO"}_${new Date().toISOString().slice(0, 10)}.xlsx`,
            meta: {
              ID: d.id, Severity: d.sev, Type: d.type, Rule: d.rule, Count: d.count,
              Evidence: d.evidence, IBP_Impact: d.ibpImpact, Cleanup: d.cleanup,
              Matched_File: matchedFile || "(none)", Violation_Field: fieldUsed || "(n/a)", Violation_Rule: rule,
            },
            rows: rows.map((r) => ({ ...r, __violation_field: fieldUsed, __violation_rule: rule })),
            note,
          });
          toast.success(`Downloaded ${d.id || "Dead object"}`);
        };
        return (
        <div key={i} className="bg-white border border-valantic-border rounded-md p-4" data-testid={`dead-${d.id || i}`}>
          <div className="flex items-center gap-2 mb-2">
            <Skull className="w-4 h-4 text-rose-600" strokeWidth={1.5} />
            <span className={cn("font-mono text-[10px] px-1.5 py-0.5 rounded-sm uppercase font-bold tracking-wider border", sevColor(d.sev))}>{d.sev}</span>
            <span className="font-mono text-xs text-valantic-muted">{d.id}</span>
            {showRowDownload(d.sev) && (
              <RowDownloadButton onClick={downloadOne} testid={`dead-dl-${d.id || i}`} />
            )}
            {d.count != null && (
              <span className="ml-auto font-display text-lg font-bold text-valantic-black">
                {d.count.toLocaleString()}<span className="text-xs font-normal text-valantic-muted ml-1">recs</span>
              </span>
            )}
          </div>
          <div className="font-medium text-valantic-black">{d.type}</div>
          <div className="font-mono text-xs text-valantic-muted mt-0.5">{d.rule}</div>
          <div className="mt-3 pt-3 border-t border-valantic-border space-y-2">
            <Field label="Evidence"   value={d.evidence} />
            <Field label="IBP impact" value={d.ibpImpact} />
            <Field label="Cleanup"    value={d.cleanup} />
          </div>
        </div>
        );
      })}
      </div>
    </div>
  );
}

function MappingTab({ gaps, sourceFiles = [] }) {
  // Only show rows whose S/4 source table is actually present among the uploaded files
  // OR rows that are explicitly "Custom" (i.e. independent of SAP standard tables).
  // This keeps the dashboard strictly grounded — no MARC/KNA1/MARM/T001W references
  // unless the user actually uploaded a file with that name.
  const allRows = Object.entries(MDT_MAP).flatMap(([mdt, attrs]) => attrs.map((a) => ({ mdt, ...a })));
  const rows = allRows.filter((a) => {
    const tab = String(a.s4Tab || "").trim();
    if (!tab || tab === "Custom" || tab === "—") return true;
    return findFilesForTable(tab, sourceFiles).length > 0;
  });

  if (!rows.length) {
    return (
      <div data-testid="mapping-empty" className="bg-white border border-valantic-border rounded-md p-6 text-sm text-valantic-muted">
        {sourceFiles.length
          ? `None of your ${sourceFiles.length} uploaded file(s) (${sourceFiles.map((f) => f.name).join(", ")}) match a standard S/4HANA master-data table. Upload the corresponding S/4 extract to populate this mapping.`
          : "Upload your S/4HANA extracts to populate this mapping table — only attributes whose source is in your uploads are shown here."}
      </div>
    );
  }
  return (
    <div className="bg-white border border-valantic-border rounded-md overflow-hidden">
      <div className="overflow-x-auto ibp-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-valantic-bg border-b border-valantic-border">
              {["MDT","Attribute","Mand.","S/4 Table","S/4 Field","Description","Status"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((a, i) => {
              const gap = gaps.find((g) => g.attr === a.attr && g.obj === a.mdt);
              const status = gap ? gap.sev : a.s4Tab === "Custom" ? "needs mapping" : "available";
              const sev = gap ? gap.sev : a.s4Tab === "Custom" ? "medium" : "low";
              return (
                <tr key={i} className="border-b border-valantic-border last:border-0 hover:bg-slate-50/60">
                  <td className="px-3 py-2 font-mono text-xs">{a.mdt}</td>
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{a.attr}</td>
                  <td className="px-3 py-2 text-xs">{a.mand ? "✓" : "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{a.s4Tab}</td>
                  <td className="px-3 py-2 font-mono text-xs">{a.s4Fld}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{a.desc}</td>
                  <td className="px-3 py-2">
                    <span className={cn("font-mono text-[10px] px-1.5 py-0.5 rounded-sm uppercase font-bold tracking-wider border", sevColor(sev))}>{status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiReadinessTab({ data, sourceFiles = [] }) {
  const entries = Object.entries(data);

  const exportXlsx = () => {
    const summaryRows = entries.map(([kf, info]) => ({
      Key_Figure: kf,
      Readiness_Pct: info.pct || 0,
      Severity_Band: (info.pct || 0) < 30 ? "critical" : (info.pct || 0) < 70 ? "high" : "low",
      Blockers: (info.blockers || []).join(" | "),
    }));
    const gapSheets = entries.map(([kf, info]) => {
      const rows = Array.isArray(info.sampleRecords) ? info.sampleRecords : (info.blockers || []).map((b) => ({ KF: kf, Blocker: b }));
      return { name: kf, rows, note: rows.length ? `${rows.length} blocker records for ${kf}` : "No blocker records" };
    });
    downloadGapsWorkbook({
      filename: `valantic_MDQ_KF_Readiness_${new Date().toISOString().slice(0, 10)}.xlsx`,
      summaryRows,
      summaryHeaders: ["Key_Figure","Readiness_Pct","Severity_Band","Blockers"],
      gapSheets,
    });
    toast.success("KF Readiness workbook downloaded");
  };

  return (
    <div className="space-y-2.5">
      <ExportToolbar
        title="KF Readiness — blocker records export"
        count={entries.length}
        helper={sourceFiles.length ? `Matching against ${sourceFiles.length} uploaded file(s)` : "Showing embedded sample records (upload source files for live data)"}
        onExport={exportXlsx}
        testid="export-kfi-xlsx-btn"
      />
      {entries.map(([kf, info]) => {
        const pct = info.pct || 0;
        const sev = pct < 30 ? "critical" : pct < 70 ? "high" : "low";
        const downloadOne = () => {
          const rows = Array.isArray(info.sampleRecords) ? info.sampleRecords : (info.blockers || []).map((b) => ({ KF: kf, Blocker: b }));
          downloadSingleGapXlsx({
            filename: `valantic_MDQ_KF_${kf}_${new Date().toISOString().slice(0, 10)}.xlsx`,
            meta: {
              Key_Figure: kf, Readiness_Pct: pct, Severity_Band: sev,
              Blockers: (info.blockers || []).join(" | "),
            },
            rows,
            note: rows.length ? `${rows.length} blocker records for ${kf}` : "No blocker records",
          });
          toast.success(`Downloaded ${kf}`);
        };
        return (
        <div key={kf} className="bg-white border border-valantic-border rounded-md p-4 grid md:grid-cols-12 gap-3 items-center" data-testid={`kf-${kf}`}>
          <div className="md:col-span-3 font-mono text-sm font-medium flex items-center gap-2">
            <span>{kf}</span>
            {showRowDownload(sev) && (
              <RowDownloadButton onClick={downloadOne} testid={`kf-dl-${kf}`} />
            )}
          </div>
          <div className="md:col-span-3 flex items-center gap-3">
            <div className={cn("font-display text-3xl font-bold", pct < 30 ? "text-rose-600" : pct < 70 ? "text-amber-600" : "text-emerald-600")}>
              {pct}%
            </div>
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={cn("h-full", pct < 30 ? "bg-rose-500" : pct < 70 ? "bg-amber-500" : "bg-emerald-500")} style={{ width: `${pct}%` }} />
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="text-[10px] uppercase tracking-widest text-valantic-muted mb-0.5 font-mono">Blockers</div>
            <ul className="text-xs text-slate-700 space-y-0.5">
              {(info.blockers || []).map((b, i) => <li key={i}>· {b}</li>)}
            </ul>
          </div>
        </div>
        );
      })}
    </div>
  );
}

function RemediationTab({ data }) {
  const phases = [
    { id: "p0", label: "P0 · Blockers (do first — everything else depends on these)", tone: "rose" },
    { id: "p1", label: "P1 · High priority (unlock core KF chain)",                  tone: "amber" },
    { id: "p2", label: "P2 · Complete master data quality",                          tone: "emerald" },
  ];
  return (
    <div className="space-y-3">
      {phases.map((p) => {
        const items = data[p.id] || [];
        if (!items.length) return null;
        return (
          <div key={p.id} className="bg-white border border-valantic-border rounded-md overflow-hidden">
            <div className={cn(
              "px-5 py-2.5 border-b border-valantic-border flex items-center gap-2",
              p.tone === "rose" && "bg-rose-50",
              p.tone === "amber" && "bg-amber-50",
              p.tone === "emerald" && "bg-emerald-50",
            )}>
              <div className={cn(
                "font-mono text-[10px] uppercase tracking-wider font-bold",
                p.tone === "rose" && "text-rose-700",
                p.tone === "amber" && "text-amber-700",
                p.tone === "emerald" && "text-emerald-700",
              )}>{p.label}</div>
              <span className="ml-auto text-xs text-valantic-muted">{items.length} action{items.length === 1 ? "" : "s"}</span>
            </div>
            <div className="divide-y divide-valantic-border">
              {items.map((a, i) => (
                <div key={i} className="px-5 py-3 grid md:grid-cols-12 gap-3 text-sm" data-testid={`remediation-${p.id}-${i}`}>
                  <div className="md:col-span-7 font-medium text-valantic-black">{a.action}</div>
                  <div className="md:col-span-2 text-xs text-valantic-muted">{a.owner}</div>
                  <div className="md:col-span-1 text-xs font-mono">{a.effort}</div>
                  <div className="md:col-span-2 text-[11px] text-slate-700 italic">{a.unlocks}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Small UI primitives
// ═══════════════════════════════════════════════════════════════════════════
const Hero = ({ eyebrow, title, sub }) => (
  <div className="bg-white border border-valantic-border rounded-md p-6 md:p-8 relative overflow-hidden">
    <div className="absolute top-0 left-0 right-0 h-0.5 val-gradient-warm" />
    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted mb-1">{eyebrow}</div>
    <h1 className="font-display text-2xl sm:text-3xl font-bold text-valantic-black tracking-tight">{title}</h1>
    {sub && <p className="text-valantic-muted mt-2 text-sm">{sub}</p>}
  </div>
);

const Section = ({ eyebrow, title, right, children }) => (
  <div className="bg-white border border-valantic-border rounded-md p-5">
    <SectionTitle eyebrow={eyebrow} title={title} right={right} />
    {children}
  </div>
);

const SectionTitle = ({ eyebrow, title, right }) => (
  <div className="flex items-end justify-between gap-4 border-b border-valantic-border pb-3 mb-4 flex-wrap">
    <div>
      {eyebrow && <div className="text-[10px] uppercase tracking-[0.18em] text-valantic-muted font-mono mb-1">{eyebrow}</div>}
      <h3 className="font-display text-base sm:text-lg font-bold text-valantic-black tracking-tight">{title}</h3>
    </div>
    {right}
  </div>
);

const NavBar = ({ onBack, onNext, nextLabel, nextDisabled = false, disabledReason }) => (
  <div className="flex items-center justify-between gap-3">
    <button onClick={onBack} className="inline-flex items-center gap-2 h-10 px-4 border border-valantic-border bg-white rounded-sm text-sm hover:bg-slate-50">
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
    <div className="flex items-center gap-3">
      {nextDisabled && disabledReason && (
        <span className="text-xs text-amber-700" data-testid="next-disabled-reason">{disabledReason}</span>
      )}
      <button
        onClick={onNext}
        disabled={nextDisabled}
        data-testid="next-step-btn"
        className={cn(
          "inline-flex items-center gap-2 h-10 px-5 rounded-sm text-sm font-semibold",
          nextDisabled
            ? "bg-slate-200 text-slate-500 cursor-not-allowed"
            : "val-gradient-warm text-white hover:shadow-md"
        )}
      >
        {nextLabel} <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
      </button>
    </div>
  </div>
);

const Badge = ({ tone = "muted", children }) => (
  <span className={cn(
    "inline-flex items-center px-2 py-0.5 rounded-sm border font-mono text-[10px] uppercase tracking-wider font-medium",
    tone === "critical" && "bg-rose-50 text-rose-700 border-rose-200",
    tone === "high"     && "bg-amber-50 text-amber-700 border-amber-200",
    tone === "medium"   && "bg-blue-50 text-blue-700 border-blue-200",
    tone === "muted"    && "bg-slate-50 text-slate-600 border-slate-200",
  )}>{children}</span>
);

const ExpertCard = ({ Icon, label, text, color }) => (
  <div className="bg-white border border-valantic-border rounded-md p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 rounded-sm flex items-center justify-center" style={{ background: color, color: "white" }}>
        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
      </div>
      <div className="font-display font-bold text-sm text-valantic-black">{label}</div>
    </div>
    <p className="text-xs text-slate-700 leading-relaxed">{text || "—"}</p>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-white border border-valantic-border rounded-md p-3">
    <div className="text-[10px] uppercase tracking-widest text-valantic-muted font-mono mb-1.5">{title}</div>
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-valantic-muted font-mono mb-0.5">{label}</div>
    <div className="text-xs text-slate-700 leading-relaxed">{value || "—"}</div>
  </div>
);

const Box = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-valantic-muted font-mono mb-0.5">{label}</div>
    <div className={cn("text-xs text-slate-700 leading-relaxed", mono && "font-mono")}>{value || "—"}</div>
  </div>
);

// ─── Guided remediation roadmap ───────────────────────────────────────────
function WhatsNextPanel({ analysis, sum, modules, product }) {
  const kpiReadiness = analysis.kpiReadiness || {};
  const rem = analysis.remediation || {};
  const mdGaps = analysis.masterDataGaps || [];
  const top = sum.top3Blockers || [];

  // Sort modules by readiness ascending (worst first) when KF readiness data is available.
  const moduleHealth = useMemo(() => {
    if (product !== "IBP" || !modules?.length) return [];
    // Map a module to its representative KFs based on the catalog.
    const map = Object.fromEntries(IBP_MODULES.map((m) => [m.name, m.kfs]));
    return modules
      .map((name) => {
        const kfs = map[name] || [];
        const pcts = kfs.map((k) => (kpiReadiness[k]?.pct ?? null)).filter((x) => x !== null);
        const avg = pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : null;
        return { name, kfs, avg };
      })
      .filter((m) => m.avg !== null)
      .sort((a, b) => a.avg - b.avg);
  }, [modules, kpiReadiness, product]);

  const weakestModule = moduleHealth[0] || null;
  const p0 = (rem.p0 || []).slice(0, 3);
  const topGaps = mdGaps.filter((g) => g.sev === "critical").slice(0, 3);

  return (
    <div className="bg-white border border-valantic-border rounded-md p-4 relative overflow-hidden h-full">
      <div className="absolute top-0 left-0 right-0 h-0.5 val-gradient-warm" />
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-sm val-gradient-warm text-white flex items-center justify-center">
          <Lightbulb className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-valantic-muted font-mono">Guided remediation</div>
          <div className="font-display text-base font-bold text-valantic-black">What to do next</div>
        </div>
      </div>

      {weakestModule && (
        <div className="mb-3 p-3 bg-rose-50 border border-rose-200 rounded-sm" data-testid="whats-next-weakest-module">
          <div className="text-[10px] font-mono uppercase tracking-wider text-rose-700 mb-0.5">Weakest module</div>
          <div className="font-medium text-sm text-valantic-black">
            <span className="font-bold">{weakestModule.name}</span>
            <span className="text-rose-700 font-display font-bold ml-2">{weakestModule.avg}%</span>
            <span className="text-valantic-muted text-xs ml-1">avg KF readiness</span>
          </div>
          {weakestModule.kfs.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {weakestModule.kfs.slice(0, 5).map((k) => (
                <span key={k} className="font-mono text-[10px] px-1.5 py-0.5 bg-white text-slate-700 border border-rose-100 rounded-sm">{k}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {p0.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-widest text-valantic-muted font-mono mb-1.5">Do first — P0 actions</div>
          <ol className="space-y-1.5">
            {p0.map((a, i) => (
              <li key={i} className="text-xs flex gap-2" data-testid={`whats-next-p0-${i}`}>
                <span className="font-mono text-[10px] text-rose-600 mt-0.5 shrink-0 font-bold">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-800 leading-snug">{a.action}</div>
                  <div className="text-[10px] text-valantic-muted mt-0.5 flex flex-wrap gap-x-2 font-mono">
                    {a.owner && <span>{a.owner}</span>}
                    {a.effort && <span>· {a.effort}</span>}
                    {a.unlocks && <span className="italic text-emerald-700">→ unlocks {a.unlocks}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {topGaps.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-valantic-muted font-mono mb-1.5">Critical gaps to attack</div>
          <div className="flex flex-wrap gap-1.5">
            {topGaps.map((g) => (
              <span key={g.id} className="text-[11px] font-mono px-2 py-1 bg-rose-50 text-rose-700 border border-rose-200 rounded-sm" data-testid={`whats-next-gap-${g.id}`}>
                {g.id} · {g.obj}.{g.attr}
              </span>
            ))}
          </div>
        </div>
      )}

      {!weakestModule && p0.length === 0 && topGaps.length === 0 && (
        <div className="text-xs text-valantic-muted py-4 text-center">
          No blockers detected. {top[0] || "Run an analysis with source data to see remediation guidance."}
        </div>
      )}
    </div>
  );
}

// ─── Run-over-run diff ────────────────────────────────────────────────────
function RunDiffPanel({ currentSum, product, modules }) {
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/mdq/runs?limit=10`);
        if (!alive) return;
        const runs = data?.runs || [];
        // Find the most recent OTHER run with same product (and overlapping modules if any).
        const prev = runs.find((r) => r.product === product && (!modules?.length || (r.modules || []).some((m) => modules.includes(m))));
        setPrevious(prev || runs[1] || null);
      } catch {
        // No previous run / unauthorized — silently ignore.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [product, modules]);

  if (loading) {
    return (
      <div className="bg-white border border-valantic-border rounded-md p-4 h-full flex items-center justify-center text-xs text-valantic-muted">
        <Loader2 className="w-3.5 h-3.5 ibp-spin-slow mr-1.5" /> Loading prior runs…
      </div>
    );
  }

  if (!previous) {
    return (
      <div className="bg-white border border-valantic-border rounded-md p-4 h-full">
        <div className="text-[10px] uppercase tracking-[0.18em] text-valantic-muted font-mono mb-1">Trend</div>
        <div className="font-display text-base font-bold text-valantic-black mb-2">First baseline run</div>
        <p className="text-xs text-valantic-muted leading-relaxed">
          No prior run on the same product. The current scores will become your baseline. Re-run after remediation to see the run-over-run delta.
        </p>
      </div>
    );
  }

  const prevSum = previous.summary || {};
  const deltas = [
    { label: "DQ score",      a: currentSum.overallScore,  b: prevSum.overallScore,  higherIsBetter: true },
    { label: "Critical gaps", a: currentSum.criticalGaps,  b: prevSum.criticalGaps,  higherIsBetter: false },
    { label: "High gaps",     a: currentSum.highGaps,      b: prevSum.highGaps,      higherIsBetter: false },
    { label: "Medium gaps",   a: currentSum.mediumGaps,    b: prevSum.mediumGaps,    higherIsBetter: false },
  ].map((d) => {
    const diff = (d.a ?? 0) - (d.b ?? 0);
    const improved = d.higherIsBetter ? diff > 0 : diff < 0;
    const worsened = d.higherIsBetter ? diff < 0 : diff > 0;
    return { ...d, diff, improved, worsened };
  });

  return (
    <div className="bg-white border border-valantic-border rounded-md p-4 h-full" data-testid="run-diff-panel">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-valantic-royal" strokeWidth={1.75} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-valantic-muted font-mono">Trend vs previous run</div>
          <div className="font-mono text-[10px] text-valantic-muted">
            {previous.created_at?.slice(0, 10)} · {previous.user_email}
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        {deltas.map((d) => (
          <div key={d.label} className="flex items-center justify-between text-xs" data-testid={`run-diff-${d.label.replace(/\s+/g, "-").toLowerCase()}`}>
            <span className="text-slate-700">{d.label}</span>
            <span className="flex items-center gap-2 font-mono">
              <span className="text-valantic-muted">{d.b ?? "—"}</span>
              <ArrowRight className="w-3 h-3 text-valantic-silver" />
              <span className="font-semibold text-valantic-black">{d.a ?? "—"}</span>
              {d.diff !== 0 && (
                <span className={cn(
                  "text-[10px] font-bold px-1.5 py-0.5 rounded-sm",
                  d.improved && "bg-emerald-50 text-emerald-700",
                  d.worsened && "bg-rose-50 text-rose-700",
                  !d.improved && !d.worsened && "bg-slate-50 text-slate-600",
                )}>
                  {d.diff > 0 ? "+" : ""}{d.diff}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
